import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import * as XLSX from "xlsx";
import moment from "moment";
import { differenceInDays, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { ACCESS_TOKEN } from "../../common/constants/local-storage-keys";
import { getLocalStorageItem } from "../../common/utils/local-storage";
import { v4 } from 'uuid';
import { PROJECT_MANAGEMENT_API } from "../../http/api/services";
import { getEmployeeDropdownData } from "../../http/requests/session";

// MUI Imports
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  Select,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { CalendarMonthRounded } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Redux Actions
import {
  getAllSessionsData,
  getUserDetailsForTrainingAndSession,
  getInvoicedMonths,
  getInvoiceTrackerData,
  getProjectById,
  getUsersProjectAllocation,
  setListItems,
  updateTimeSheetStatus,
  getNotifications,
  getResourcingData,
  getAllUserDetails,
  getAllActiveUsersWithAllocation,
  getAssignedProjects,
  getClientAdminData,
  getSOWData,
  getClientsData,
  setSowListClientFilterId,
  getListItems,
  clonePreviousWeekTimesheet,
  getMyTimeSheetData,
  getProjectAdminData,
  getProjectAllocationData,
  getProjectTasks,
  getReportees,
  getTimeSheetData,
  getTimesheetProjects,
  getTimeSheetReportee,
  saveTimeSheetPeriodData,
  setSelectedEmployeeId,
  setSelectedProjectId,
  setTimeSheetPeriodWeek,
  submitPeriodForApproval,
  getSessionUserHistory,
  getSessionFeedbackQuestionsList,
} from "../../redux/actions";
import {
  getAllProjectManagementData,
  getMappedProjectManagementData,
  getProjectManagementData,
} from "../../redux/actions/project-management";
import {
  getViewAttendanceData,
  setViewAttendanceData,
} from "../../redux/actions/view-attendance";
import { getSkillDataGrid } from "../../redux/actions/skills";
import {
  getMissingTimesheet,
  getTimesheetReports,
  getAllocationChangeReport,
  getAllocationChangeReportByEmployee,
  setReportingData,
  resetReportingDataState,
  setAllocationChangeReportData,
  setAllocationChangeReportByEmployee,
} from "../../redux/actions/reporting";

// Internal Components
import { DataTable } from "../DataTable/DataTable";
import { CreateCalendar } from "../Calendar/Calendar";
import { SessionCreation } from "../Session/SessionCreation";
import SessionFeedback from "../Session/SessionFeedback";
import CorrectAttendanceForm from "../CorrectAttendanceForm/CorrectAttendanceForm";
import MonthYearPopup from "../ReasonDialogBox/MonthYearPopup";
import ResourceDashboardView from "../ResourceDashboard/ResourceDashboardView";
// import DeviationsView from "../Deviations/DeviationsView";
import ExpiringView from "../Expiring/ExpiringView";
import ProjectionsView from "../Projections/ProjectionsView";
import BenchView from "../Bench/BenchView";
import MultiProjectView from "../MultiProject/MultiProjectView";
import SessionReporting from "../SessionReporting/SessionReporting";
import SOWAddModal from "../SOWAddModal/SOWAddModal";
import LinkProjectsModal from "../LinkProjectsModal/LinkProjectsModal";
import SOWAllocationModal from "../SOWAllocationModal/SOWAllocationModal";
import SOWEditModal from "../SOWEditModal/SOWEditModal";
import ProjectEditModal from "../ProjectEditModal/ProjectEditModal";

// Common Utilities & Constants
import { Modules } from "../../common/constants/sidebar";
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
} from "../../common/constants/extra-constants";
import { searchIcon, excelAllIcon } from "../../common/icons";
import {
  getFullName,
  getLabel,
  getMinWidth,
  getTotalHrs,
  tableColumnsData,
  titleCase,
  UniqueIds,
} from "../../common/utils/datatable";
import { toastOptions } from "../../common/utils/toasterOptions";
import { handleSetRows } from "../../common/utils/tabsTable";
import {getMonthlyProjections} from "../../http/requests/project-admin";

// CSS
import "./tabsTable.css";
import "./ProjectFilter.css";
const getPeriods = () => {
  let date = moment().subtract(28, "days");
  const periods = [];
  for (let i = 0; i < 9; i++) {
    periods.push({
      startDate: moment(date.startOf("isoweek")),
      endDate: moment(date.add(7, "days").startOf("week")),
    });
    date.add(7, "days");
  }
  return periods;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const TabsTable = ({
  headingName,
  tabName,
  status,
  projectId,
  queryPeriodWeek,
}) => {
  const {
    clientAdminData,
    sowData,
    projectAdminData,
    projectAllocationData,
    timeSheetData,
    allProjectManagementData,
    projectManagementData,
    mappedProjectManagementData,
    selectedProjectId,
    reportees,
    reportingData,
    invoiceTrackerData,
    selectedProjectData,
    invoicedMonths,
    allocationChangeReportData,
    viewAttendanceData,
    allocationChangeReportDataByEmployee,
    sessionDetails,
    sessionUserHistoryData,
  } = useSelector(({ MODULES }) => MODULES);
  const { allUserDetails, userData, allUserDetailsDict, allActiveUsersWithAllocation } = useSelector(({ USER }) => USER);
  const { allTasks, listItems, clientsData, timeSheetProjects, skillsList, sowListClientFilterId } =
    useSelector(({ MODULES }) => MODULES);
  const dispatch = useDispatch();
  const { vTrackLoader } = useSelector(({ APP_STATE }) => APP_STATE);
  
  const [isAddButtonClicked, setIsAddButtonClicked] = useState(false);
  const [openSOWAddModal, setOpenSOWAddModal] = useState(false);
  const [linkProjectsModalOpen, setLinkProjectsModalOpen] = useState(false);
  const [linkProjectsSowRow, setLinkProjectsSowRow] = useState(null);
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [allocationSowRow, setAllocationSowRow] = useState(null);
  const [sowEditModalOpen, setSowEditModalOpen] = useState(false);
  const [sowEditModalRow, setSowEditModalRow] = useState(null);
  const [projectEditModalOpen, setProjectEditModalOpen] = useState(false);
  const [projectEditModalRow, setProjectEditModalRow] = useState(null);
  const [isEditButtonClicked, setIsEditButtonClicked] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [searchData, setSearchData] = useState("");
  const periodWeeks = getPeriods();
  const [selectedPeriodWeek, setSelectedPriodWeek] = useState(
    queryPeriodWeek !== null
      ? queryPeriodWeek
      : {
          startDate: moment().startOf("isoweek"),
          endDate: moment().endOf("isoweek"),
        }
  );
  const [selectedProject, setSelectedProject] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [rowToBeUpdated, setRowToBeUpdated] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [disableDate, setDisableDate] = useState(false);
  const [disablePeriodWeek, setDisablePeriodWeek] = useState(false);
  const [filterProjectDropdownData, setFilterProjectDropdownData] =
    useState(null);
  const [filterClientDropdownData, setFilterClientDropdownData] =
    useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientProject, setSelectedClientProject] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [employeeDropdownData, setEmployeeDropdownData] = useState([]);
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedPriSecSkills, setSelectedPriSecSkills] = useState([]);
  const [selectedStreams, setSelectedStreams] = useState([]);

  const [isHistory, setIsHistory] = useState(false);
  const [billStatus, setBillStatus] = useState(null);
  const [expiredAllocations, setExpiredAllocations] = useState(false);
  const [selectedClientFilter, setSelectedClientFilter] = useState(null);

  const [selectedUserIds, setSelectedUserIds] = useState("");
  const [selectedUserID, setSelectedUserID] = useState("");
  const [viewAttendanceClicked, setViewAttendanceClicked] = useState(false);

  const [selectedSkillsIds, setSelectedSkillsIds] = useState("");
  const [selectedPriSecSkillsIds, setSelectedPriSecSkillsIds] = useState("");
  const [selectedStreamsIds, setSelectedStreamsIds] = useState("");
  const [userSkills, setUserSkills] = useState(
    listItems ? listItems.Skills : []
  );
  const [userStreams, setUserStreams] = useState(
    listItems ? listItems.Streams : []
  );
  const [userPriSecSkills, setUserPriSecSkills] = useState(
    listItems ? listItems.PriSecSkills : []
  );

  const [userMembers, setUserMembers] = useState(allUserDetails);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllPriSecSkill, setSelectAllPriSecSkill] = useState(false);
  const [selectAllStreams, setSelectAllStreams] = useState(false);
  const [missingReportData, setMissingReportData] = useState(0);
  const [selectedMissingProjects, setSelectedMissingProjects] = useState([]);
  const [selectedTimesheetProjects, setSelectedTimesheetProjects] = useState(
    []
  );
  const [selectMissingProjectsIds, setSelectMissingProjectsIds] = useState([]);
  const [selectTimesheetProjectsIds, setSelectTimesheetProjectsIds] =
    useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [skillsLength, setSkillsLength] = useState(0);
  const [opencreateSessionPopup, setOpencreateSessionPopup] = useState(false);
  const [timesheetIds, setTimeSheetDetailIDs] = useState([]);
  const [selectedTimesheetRows, setSelectedTimesheetRows] = useState([]);
  const [selectedClientInvoiceDetail, setSelectedClientInvoiceDetail] =
    useState(null);
  const [selectedProjectInvoiceDetail, setSelectedProjectInvoiceDetail] =
    useState(null);
  const [projectsForInvoiceDropdown, setProjectsForInvoiceDropdown] =
    useState(null);
  const [clientsForInvoiceDropdown, setClientsForInvoiceDropdown] =
    useState(null);
  const [startDateInvoice, setStartDateInvoice] = useState(null);
  const [endDateInvoice, setEndDateInvoice] = useState(null);

  const [openDialogBox, setOpenDialogBox] = useState(false);

  
  // Bulk upload states
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [generalErrorMessage, setGeneralErrorMessage] = useState(null);
  
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allBillStatuses, setAllBillStatuses] = useState([]);
  const [selectedBillStatuses, setSelectedBillStatuses] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [resourceCountData, setResourceCountData] = useState([]);
  const [deviationData, setDeviationData] = useState([]);
  const [billStatusSummary, setBillStatusSummary] = useState([]);
  const [projectSummary, setProjectSummary] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiringResources, setExpiringResources] = useState({
    next10days: [],
    next20days: [],
    next30days: [],
    expired: [],
  });

  const [showDialogBox, setShowDialogBox] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [benchResources, setBenchResources] = useState([]);
  const [benchStreamData, setBenchStreamData] = useState([]);
  const [monthlyProjections, setMonthlyProjections] = useState([]);

  const inputRef = useRef("");

  const handleDialogBox = (res) => {
    setOpenDialogBox(!res);
  };

  useEffect(() => {
    if (startDate === null && endDate === null) {
      setDisablePeriodWeek(false);
    }
  }, [startDate, endDate]);

  const handleUserChangeForViewAttendance = (event) => {
    setSelectedUserID(event.target.value.id);

    setSelectedUser(event.target.value);
  };

  const handleClose = () => {
    setOpencreateSessionPopup(false);
    setOpenDialogBox(false);
  };

  const handleMultipleFileView = (res) => {
    setOpencreateSessionPopup(!res);
  };

  const handleViewModalClose = () => {
    setShowDialogBox(false);
  };
  
  const handleViewButtonClick= ()=>{
    setShowDialogBox(true);
    processMonthlyProjections();
  }
  // Helper for 'View Projections' button
const createViewButton = (label) => (
  <button className="addBtn viewButton" onClick={handleViewButtonClick}>
    {label}
  </button>
);

  const getCounts = () => ({
    // deviations: data.deviations.filter((d) => d.status !== "normal").length,
    expiring: data.expiringAllocations.length,
    projections: data.monthlyProjections.length,
    bench: data.benchResources.length,
  });

  const getDataInfo = () => {
    const counts = getCounts();
    return [
      // {
      //   text: `${counts.deviations} Deviations Found`,
      //   className: "deviation-badge",
      // },
      { text: `${counts.expiring} Expiring Soon`, className: "expiring-badge" },
      {
        text: `${counts.projections} Months Projected`,
        className: "projections-badge",
      },
    ];
  };

  const getData = (event) => {
    if (headingName === Modules.REPORTING) {
      if (tabName === "ALLOCATION CHANGE REPORT BY PROJECT") {
        if (!selectedProjectId || !startDate || !endDate) {
          toast.info(
            "Please Select Project ID , Start and End Date",
            toastOptions
          );
          return;
        }
      }
      if (tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE") {
        if (!selectedUserIds || !startDate || !endDate) {
          toast.info("Please Select User , Start and End Date", toastOptions);
        }
      }
      if (!disableDate && !disablePeriodWeek) {
        toast.info(
          "Please Select Period Week OR Start and End Date",
          toastOptions
        );
        return;
      }
      if (tabName === "TIMESHEET REPORTS" && !selectTimesheetProjectsIds) {
        toast.info("Please select projects", toastOptions);
        return;
      }
      if (
        tabName === "ALLOCATION CHANGE REPORT BY PROJECT" &&
        !selectedProjectId
      ) {
        toast.info("Please select projects", toastOptions);
        return;
      }
      if (
        tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE" &&
        !selectedUserIds
      ) {
        toast.info("Please select user", toastOptions);
        return;
      }
      if (tabName === "ALLOCATION CHANGE REPORT BY PROJECT") {
      }
      if (!disableDate) {
        if (!startDate) {
          toast.info("Please Enter Start Date", toastOptions);
          return;
        } else if (!endDate) {
          toast.info("Please Enter End Date", toastOptions);
          return;
        } else {
          if (startDate.isValid() && endDate.isValid()) {
            if (tabName === "MISSING TIMESHEET") {
              dispatch(
                getMissingTimesheet({
                  startDate: startDate.format(DATE_TIME_FORMAT),
                  endDate: endDate.format(DATE_TIME_FORMAT),
                  projectId: Array.isArray(selectMissingProjectsIds) ? selectMissingProjectsIds.join(',') : selectMissingProjectsIds,
                })
              );
            } else if (tabName === "ALLOCATION CHANGE REPORT BY PROJECT") {
              dispatch(
                getAllocationChangeReport({
                  startDate: startDate.format(DATE_TIME_FORMAT),
                  endDate: endDate.format(DATE_TIME_FORMAT),
                  projectId: selectedProjectId,
                })
              );
            } else if (tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE") {
              dispatch(
                getAllocationChangeReportByEmployee({
                  employeeIds: selectedUserIds,
                  endDate: endDate.format(DATE_TIME_FORMAT),
                  startDate: startDate.format(DATE_TIME_FORMAT),
                })
              );
            } else {
              dispatch(
                getTimesheetReports({
                  startDate: startDate.format(DATE_TIME_FORMAT),
                  endDate: endDate.format(DATE_TIME_FORMAT),
                  projectId: selectTimesheetProjectsIds,
                })
              );
            }
          } else {
            toast.info("Please Enter Valid Date", toastOptions);
            return;
          }
        }
      }
      if (!disablePeriodWeek) {
        tabName === "MISSING TIMESHEET"
          ? dispatch(
              getMissingTimesheet({
                startDate: moment(selectedPeriodWeek.startDate).format(
                  DATE_TIME_FORMAT
                ),
                endDate: moment(selectedPeriodWeek.endDate).format(
                  DATE_TIME_FORMAT
                ),
                projectId: Array.isArray(selectMissingProjectsIds) ? selectMissingProjectsIds.join(',') : selectMissingProjectsIds,
              })
            )
          : dispatch(
              getTimesheetReports({
                startDate: moment(selectedPeriodWeek.startDate).format(
                  DATE_TIME_FORMAT
                ),
                endDate: moment(selectedPeriodWeek.endDate).format(
                  DATE_TIME_FORMAT
                ),
                projectId: selectTimesheetProjectsIds,
              })
            );
      }
    }
  };

  const getInvoiceDetailsData = (event) => {
    if (startDateInvoice && !endDateInvoice) {
      toast.info("Please Select End Date", toastOptions);
      return;
    }
    if (endDateInvoice && !startDateInvoice) {
      toast.info("Please Select Start Date", toastOptions);
      return;
    }
    dispatch(
      getInvoiceTrackerData({
        clientId: selectedClientInvoiceDetail
          ? selectedClientInvoiceDetail.id
          : "",
        projectId: selectedProjectInvoiceDetail
          ? selectedProjectInvoiceDetail.id
          : "",
        startDate: startDateInvoice ? startDateInvoice.format(DATE_FORMAT) : "",
        endDate: endDateInvoice ? endDateInvoice.format(DATE_FORMAT) : "",
      })
    );
  };

  const getSkillData = (event) => {
    if (headingName === Modules.SKILLS) {
      dispatch(
        getSkillDataGrid({
          Streams: selectedStreamsIds,
          SkillBuckets: selectedSkillsIds,
          PrimarySecondarySkills: selectedPriSecSkillsIds,
        })
      );
    }
  };

  const projectsTimesheetDropdown = () => {
    let projects = [];
    if (mappedProjectManagementData) {
      mappedProjectManagementData.forEach((client) => {
        client.projects.forEach((project) => {
          projects.push({
            projectName: project.projectName,
            projectId: project.projectId,
          });
        });
      });

      projects = projects.sort(function (a, b) {
        return a.projectName.localeCompare(b.projectName);
      });
    }

    return (
      <>
        <TextField
          select
          label={"Select Projects"}
          value={selectedTimesheetProjects}
          required
          sx={{
            minWidth: "20rem",
            marginRight: "1rem",
            "& label": {
              lineHeight: "1rem",
            },
          }}
          SelectProps={{
            multiple: true, // Enable multiple selection
            onChange: (event) => {
              const selectedValues = event.target.value;
              setSelectedTimesheetProjects(selectedValues);
            },
          }}
        >
          {projects &&
            projects.map((option) => (
              <MenuItem
                key={option.projectId}
                value={option.projectName}
                required
                onClick={() => {
                  if (!selectTimesheetProjectsIds.includes(option.projectId)) {
                    setSelectedTimesheetProjects((prev) => [
                      ...prev,
                      {
                        projectId: option.projectId,
                        projectName: option.projectName,
                      },
                    ]);
                    setSelectTimesheetProjectsIds((prev) => [
                      ...prev,
                      option.projectId,
                    ]);
                    // dispatch(setSelProjectsIds(option.projectId));
                  } else {
                    const newProjectIds = selectTimesheetProjectsIds.filter(
                      (id) => id !== option.projectId
                    );
                    setSelectTimesheetProjectsIds(newProjectIds);
                    const newProjects = selectedTimesheetProjects.filter(
                      (obj) => obj.projectId !== option.projectId
                    );
                    setSelectedTimesheetProjects(newProjects);
                  }
                }}
              >
                {option.projectName}
              </MenuItem>
            ))}
        </TextField>
      </>
    );
  };

  // Helper function to render project filter chips
  const renderProjectFilterChips = (selectedProjects, onRemove, onClearAll) => {
    const handleRemoveProject = (projectId) => {
      const newProjectIds = selectMissingProjectsIds.filter(
        (id) => id !== projectId
      );
      setSelectMissingProjectsIds(newProjectIds);
      const newProjects = selectedMissingProjects.filter(
        (obj) => obj.projectId !== projectId
      );
      setSelectedMissingProjects(newProjects);
    };

    const handleClearAll = () => {
      setSelectedMissingProjects([]);
      setSelectMissingProjectsIds([]);
    };

    return (
      <div className="project-filter-container">
        <div className="project-filters">
          {selectedProjects && selectedProjects.length > 0 ? (
            <>
              <span className="project-filter-label">Selected:</span>
              {selectedProjects.map((project) => (
                <div key={project.projectId} className="project-filter-chip">
                  <span>{project.projectName}</span>
                  <button
                    className="project-filter-remove"
                    onClick={() => handleRemoveProject(project.projectId)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedProjects.length > 1 && (
                <button
                  className="project-filter-clear-all"
                  onClick={handleClearAll}
                >
                  Clear All
                </button>
              )}
            </>
          ) : (
            <span className="project-filter-empty">No projects selected - use the dropdown above to add projects</span>
          )}
        </div>
      </div>
    );
  };

  const projectsMissingDropdown = () => {
    let projects = [];
    if (mappedProjectManagementData) {
      mappedProjectManagementData.forEach((client) => {
        client.projects.forEach((project) => {
          projects.push({
            projectName: project.projectName,
            projectId: project.projectId,
          });
        });
      });

      projects = projects.sort(function (a, b) {
        return a.projectName.localeCompare(b.projectName);
      });
    }

    return (
      <>
        <TextField
          select
          label={"Select Projects"}
          placeholder="Choose projects to filter..."
          value={[]}
          required
          sx={{
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem",
            marginRight: "1rem",
            "& label": {
              lineHeight: "1rem",
            },
            "& .MuiSelect-select": {
              minHeight: "1.4375em",
              height: "1.4375em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            },
          }}
          SelectProps={{
            multiple: true, // Enable multiple selection
            onChange: (event) => {
              const selectedValues = event.target.value;
              // This will be handled by the onClick of MenuItem
            },
          }}
        >
          {projects &&
            projects.map((option) => (
              <MenuItem
                key={option.projectId}
                value={option.projectName}
                required
                selected={selectMissingProjectsIds.includes(option.projectId)}
                onClick={() => {
                  if (!selectMissingProjectsIds.includes(option.projectId)) {
                    setSelectedMissingProjects((prev) => [
                      ...prev,
                      {
                        projectId: option.projectId,
                        projectName: option.projectName,
                      },
                    ]);
                    setSelectMissingProjectsIds((prev) => [
                      ...prev,
                      option.projectId,
                    ]);
                    // dispatch(setSelProjectsIds(option.projectId));
                  } else {
                    const newProjectIds = selectMissingProjectsIds.filter(
                      (id) => id !== option.projectId
                    );
                    setSelectMissingProjectsIds(newProjectIds);
                    const newProjects = selectedMissingProjects.filter(
                      (obj) => obj.projectId !== option.projectId
                    );
                    setSelectedMissingProjects(newProjects);
                  }
                }}
              >
                <Checkbox checked={selectMissingProjectsIds.includes(option.projectId)} />
                {option.projectName}
              </MenuItem>
            ))}
        </TextField>
      </>
    );
  };
  const projectsDropdownAllocationReport = () => {
    let projects = [];
    if (mappedProjectManagementData) {
      mappedProjectManagementData.forEach((client) => {
        client.projects.forEach((project) => {
          projects.push({
            projectName: project.projectName,
            projectId: project.projectId,
          });
        });
      });

      projects = projects.sort(function (a, b) {
        return a.projectName.localeCompare(b.projectName);
      });
    }

    return (
      <>
        <TextField
          select
          label={"Projects"}
          value={selectedProject.projectName ? selectedProject.projectName : ""}
          required
          sx={{
            minWidth: "20rem",
            marginRight: "1rem",
            "& label": {
              lineHeight: "1rem",
            },
          }}
        >
          {projects &&
            projects.map((option) => (
              <MenuItem
                key={option.projectId}
                value={option.projectName}
                required
                onClick={() => {
                  setSelectedProject({
                    projectId: option.projectId,
                    projectName: option.projectName,
                  });
                  dispatch(setSelectedProjectId(option.projectId));
                }}
              >
                {option.projectName}
              </MenuItem>
            ))}
        </TextField>
      </>
    );
  };

  const employeeDropdownAllocationChangeReport = () => {
    return (
      <>
        <div className="dropdownsBox">
          <label style={{ marginRight: "1rem", marginTop: "-0.5rem" }}>
            Select Users
          </label>
          <FormControl
            sx={{
              width: 200,
              marginTop: "-0.8rem",
              minWidth: "30rem",
              marginRight: "1rem",
              "& label": { lineHeight: "1rem" },
            }}
          >
            <InputLabel required id="demo-multiple-checkbox-label">
              Select User
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              value={selectedUsers}
              onChange={handleUserChange}
              input={<OutlinedInput label="Select User" />}
              renderValue={getUsersNames}
              MenuProps={MenuProps}
            >
              {allUserDetails && (
                <ListSubheader className="subheader">
                  <TextField
                    placeholder="Search Here..."
                    className="subheader-field"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    autoFocus={true}
                    onChange={(e) => {
                      setUserMembers({
                        ...allUserDetails,
                        data: allUserDetails.data.filter(
                          (i) =>
                            i.firstName
                              ?.toLowerCase()
                              .includes(e.target.value.toLowerCase()) ||
                            i.lastName
                              ?.toLowerCase()
                              .includes(e.target.value.toLowerCase()) ||
                            i.email
                              ?.toLowerCase()
                              .includes(e.target.value.toLowerCase())
                        ),
                      });
                    }}
                  />
                </ListSubheader>
              )}
              <div
                disabled={!selectedUserIds.length}
                style={{
                  textAlign: "right",
                  cursor: "pointer",
                  height: "2rem",
                }}
              >
                <Link
                  onClick={() => {
                    setSelectedUsers([]);
                    setSelectedUserIds([]);
                    setSelectAll(false);
                  }}
                  style={{ color: "#1773bc", paddingRight: "1rem" }}
                >
                  Clear Selection
                </Link>
              </div>
              <MenuItem
                key={`select-all`}
                value={"Select All"}
                className="no-left-margin"
              >
                <Checkbox checked={selectAll} />
                <ListItemText
                  primary={selectAll ? "Select None" : "Select All"}
                />
              </MenuItem>
              {userMembers &&
                userMembers.data.map((user) => (
                  <MenuItem
                    key={user.id}
                    value={user}
                    className="no-left-margin"
                  >
                    <Checkbox
                      checked={
                        selectedUsers.findIndex(
                          (person) => person.id === user.id
                        ) > -1
                      }
                    />
                    <ListItemText
                      primary={
                        getFullName(user.firstName, user.lastName) +
                        " (" +
                        user.email +
                        ")"
                      }
                    />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      </>
    );
  };
  const createStartDateEndDateContainer = () => {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        sx={{
          "& label": {
            lineHeight: "0.8rem",
          },
        }}
      >
        <DatePicker
          style={{
            height: "3rem",
          }}
          label="Start Date"
          value={startDate}
          onChange={(newValue) => {
            setDisablePeriodWeek(true);
            setDisableDate(false);
            setStartDate(newValue);
          }}
          renderInput={(params) => (
            <TextField
              style={{
                width: "15rem",
              }}
              {...params}
            />
          )}
          // disabled={disableDate}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={(newValue) => {
            setDisablePeriodWeek(true);
            setDisableDate(false);
            setEndDate(newValue);
          }}
          renderInput={(params) => (
            <TextField
              style={{
                width: "15rem",
              }}
              {...params}
            />
          )}
          //disabled={disableDate}
        />
      </LocalizationProvider>
    );
  };

  const createStartDateEndDateContainerInvoiceDetails = () => {
    return (
      <div style={{ display: "flex" }}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          sx={{
            "& label": {
              lineHeight: "0.8rem",
            },
          }}
        >
          <div>
            <DatePicker
              style={{
                height: "3rem",
              }}
              label="Start Date"
              value={startDateInvoice}
              onChange={(newValue) => {
                setStartDateInvoice(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  style={{
                    width: "15rem",
                  }}
                  {...params}
                />
              )}
            />
          </div>
          <div style={{ marginLeft: "1rem" }}>
            <DatePicker
              label="End Date"
              value={endDateInvoice}
              onChange={(newValue) => {
                setEndDateInvoice(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  style={{
                    width: "15rem",
                  }}
                  {...params}
                />
              )}
              //disabled={disableDate}
            />
          </div>
        </LocalizationProvider>
      </div>
    );
  };
  const createClearAndShowButton = () => {
    return (
      <>
        <button
          onClick={(event) => getData()}
          className={
            tabName === "TIMESHEET REPORTS"
              ? selectTimesheetProjectsIds
                ? "showDataBtn"
                : "disableAddButton"
              : "showDataBtn"
          }
        >
          Show Data
        </button>

        <button
          style={{ marginLeft: 0 }}
          className={
            disableDate || disablePeriodWeek
              ? "addBtn showDataBtn"
              : "disableAddButton"
          }
          onClick={() => {
            setDisableDate(false);
            setStartDate(null);
            setEndDate(null);
            setDisablePeriodWeek(false);
            dispatch(setSelectedProjectId(null));
            setSelectMissingProjectsIds([]);
            setSelectTimesheetProjectsIds("");
            setSelectedMissingProjects([]);
            setSelectedTimesheetProjects([]);
            setSelectedProject({
              projectName: null,
              projectId: null,
            });
            // setSelectedPriodWeek({
            //   startDate : moment().startOf('isoweek'),
            //   endDate : moment().day() === 0 ? moment().startOf('week') : moment().add(7,'days').startOf('week')
            // });
          }}
        >
          Clear Selection
        </button>
      </>
    );
  };

  const createClearAndShowButtonForSkills = () => {
    return (
      <div className="skills-btns">
        <button
          style={{ width: "16rem", marginleft: "0rem" }}
          onClick={(event) => getSkillData()}
          className={
            tabName === "SKILLS REPORT"
              ? selectedStreamsIds.length ||
                selectedSkillsIds.length ||
                selectedPriSecSkillsIds.length
                ? "showDataBtn"
                : "disableAddButton"
              : "showDataBtn"
          }
        >
          Show Data
        </button>
        <button
          style={{ marginLeft: "0rem", width: "16rem" }}
          className={"addBtn showDataBtn"}
          onClick={() => {
            setSelectedStreams([]);
            setSelectedStreamsIds("");
            setSelectAllStreams(false);
            setSelectedSkills([]);
            setSelectedSkillsIds("");
            setSelectAll(false);
            setSelectedPriSecSkills([]);
            setSelectedPriSecSkillsIds("");
            setSelectAllPriSecSkill(false);
          }}
        >
          Clear Selection
        </button>
      </div>
    );
  };

  const createClearAndShowButtonForInvoiceTracker = () => {
    return (
      <div className="skills-btns">
        <button
          style={{ width: "16rem" }}
          onClick={(event) => getInvoiceDetailsData()}
          className={
            selectedProjectInvoiceDetail ||
            selectedClientInvoiceDetail ||
            startDateInvoice ||
            endDateInvoice
              ? "showDataBtn"
              : "disableShowInvoiceDetailButton"
          }
        >
          Show Data
        </button>
        <button
          style={{ marginLeft: "0rem", width: "16rem" }}
          className={
            selectedProjectInvoiceDetail ||
            selectedClientInvoiceDetail ||
            startDateInvoice ||
            endDateInvoice
              ? "showDataBtn"
              : "disableShowInvoiceDetailButton"
          }
          onClick={() => {
            setStartDateInvoice(null);
            setEndDateInvoice(null);
            setSelectedClientInvoiceDetail(null);
            setSelectedProjectInvoiceDetail(null);
            dispatch(
              getInvoiceTrackerData({
                clientId: "",
                projectId: "",
                startDate: "",
                endDate: "",
              })
            );
          }}
        >
          Clear Selection
        </button>
      </div>
    );
  };

  const FilterData = () => {
    return (
      <>
        <div className={`filter-container ${tabName === "MISSING TIMESHEET" ? "missing-timesheet-filter" : ""}`}>
          {tabName === "TIMESHEET REPORTS" && projectsTimesheetDropdown()}
          {tabName === "MISSING TIMESHEET" && projectsMissingDropdown()}
          {tabName === "ALLOCATION CHANGE REPORT BY PROJECT" &&
            projectsDropdownAllocationReport()}
          {tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE" &&
            employeeDropdownAllocationChangeReport()}
          {periodWeek(true)}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
            }}
          >
            {tabName !== "ALLOCATION CHANGE REPORT BY PROJECT" &&
              tabName !== "ALLOCATION CHANGE REPORT BY EMPLOYEE" && (
                <span
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "600",
                    color: "gray",
                  }}
                >
                  OR
                </span>
              )}
          </div>
          {createStartDateEndDateContainer()}
          {/* {tabName === 'MISSING TIMESHEET' } */}
          {createClearAndShowButton()}
        </div>

        {/* Project Filter Chips for Missing Timesheet - Show below the form fields */}
        {tabName === "MISSING TIMESHEET" && selectedMissingProjects && selectedMissingProjects.length > 0 && (
          <div style={{ width: '100%', marginTop: '0.75rem' }}>
            {renderProjectFilterChips(selectedMissingProjects)}
          </div>
        )}

        {tabName === "MISSING TIMESHEET" ? (
          createSearchBoxforMissingTimesheet("Search")
        ) : (
          <></>
        )}
      </>
    );
  };

  const periodWeek = (flag) => {
    if (
      tabName === "ALLOCATION CHANGE REPORT BY PROJECT" ||
      tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE"
    ) {
    } else
      return (
        <Select
          IconComponent={CalendarMonthRounded}
          defaultValue={
            queryPeriodWeek !== null
              ? queryPeriodWeek.startDate.format(DATE_FORMAT) +
                " - " +
                queryPeriodWeek.endDate.format(DATE_FORMAT)
              : moment().startOf("isoweek").format(DATE_FORMAT) +
                " - " +
                moment().endOf("isoweek").format(DATE_FORMAT)
          }
          sx={{ minWidth: "15rem", maxHeight: "3rem" }}
          className={"select-date"}
          disabled={
            headingName === Modules.REPORTING ? disablePeriodWeek : false
          }
        >
          {getDateItems(flag)}
        </Select>
      );
  };

  const pendingApprovalReportessTab = () => {
    let timesheetDetailIDs;
    if (tabName === "PENDING APPROVAL") {
      timesheetDetailIDs =
        timeSheetData &&
        handleSetRows(timeSheetData, "PENDING APPROVAL")
          .map((row) => row[UniqueIds[headingName.replace(" ", "")]])
          .join(",");
    }
    // setTimeSheetDetailIDs(timesheetDetailIDs);
    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <div className="pendingApprovalDiv">
          <div className="filter-container">
            <TextField
              select
              label={"Projects"}
              value={
                selectedProject.projectName ? selectedProject.projectName : ""
              }
              sx={{
                minWidth: "15rem",
                "& label": {
                  lineHeight: "0.8rem",
                },
              }}
            >
              {timeSheetProjects &&
                timeSheetProjects.map(
                  (option) => (
                    // data.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.name}
                      required
                      onClick={() => {
                        setSelectedProject({
                          projectId: option.id,
                          projectName: option.name,
                        });
                        dispatch(setSelectedProjectId(option.id));
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  )
                  // ))
                )}
            </TextField>
            <TextField
              select
              label={"Employee Name"}
              value={
                selectedEmployee.employeeName
                  ? selectedEmployee.employeeName
                  : ""
              }
              sx={{
                minWidth: "15rem",
                "& label": {
                  lineHeight: "1rem",
                  marginTop: "-0.1rem",
                },
              }}
            >
              {reportees &&
                reportees.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={getFullName(option.firstName, option.lastName)}
                    onClick={() => {
                      setSelectedEmployee({
                        employeeId: option.id,
                        employeeName: getFullName(
                          option.firstName,
                          option.lastName
                        ),
                      });
                      dispatch(setSelectedEmployeeId(option.id));
                    }}
                  >
                    {getFullName(option.firstName, option.lastName)}
                  </MenuItem>
                ))}
            </TextField>
            {tabName !== "PENDING APPROVAL" && periodWeek(false)}
            <button
              className={"addBtn showDataBtn"}
              onClick={() => {
                if (tabName === "REPORTEES") {
                  dispatch(
                    getTimeSheetReportee({
                      periodWeek:
                        selectedPeriodWeek.startDate.format(DATE_FORMAT) +
                        " - " +
                        selectedPeriodWeek.endDate.format(DATE_FORMAT),
                      projectId: selectedProject.projectId
                        ? selectedProject.projectId
                        : "",
                      employeeId: selectedEmployee.employeeId
                        ? selectedEmployee.employeeId
                        : "",
                    })
                  );
                } else {
                  setSelectedTimesheetRows([]);
                  dispatch(
                    getTimeSheetData({
                      periodWeek:
                        tabName === "PENDING APPROVAL"
                          ? "0"
                          : selectedPeriodWeek.startDate.format(DATE_FORMAT) +
                            " - " +
                            selectedPeriodWeek.endDate.format(DATE_FORMAT),
                      employeeId: selectedEmployee.employeeId,
                      projectId: selectedProject.projectId,
                    })
                  );
                }
              }}
            >
              Show Data
            </button>
            <button
              className={
                selectedEmployee.employeeId || selectedProject.projectId
                  ? "addBtn showDataBtn"
                  : "disableAddButton"
              }
              onClick={() => {
                setSelectedEmployee({});
                dispatch(setSelectedEmployeeId(null));
                setSelectedProject({});
                dispatch(setSelectedProjectId(null));
              }}
            >
              Clear Selection
            </button>
          </div>
          {tabName === "PENDING APPROVAL" && (
            <div className="c">
              <button
                className={
                  timesheetDetailIDs && selectedTimesheetRows.length
                    ? "addBtn showDataBtn"
                    : "disableAddButton"
                }
                onClick={() => {
                  if (selectedTimesheetRows) {
                    dispatch(
                      updateTimeSheetStatus({
                        timesheetDetailIDs: selectedTimesheetRows.join(","),
                        timesheetstatus: "Approved",
                      })
                    );
                    setSelectedTimesheetRows([]);
                  }
                }}
                disabled={!timesheetDetailIDs || !selectedTimesheetRows}
              >
                Approve selected
              </button>
            </div>
          )}
        </div>
        {tabName === "PENDING APPROVAL" && timeSheetData && rows && (
          <div className="bottomSpan">
            <span style={{ color: "#808080" }}>
              {" "}
              Number of Pending Approvals :
            </span>
            <span style={{ color: "#1773bc" }}>&nbsp;{rows.length}</span>
          </div>
        )}
      </div>
    );
  };
  const myTimeSheetTab = () => {
    const periodStatus = timeSheetData && timeSheetData.length ? timeSheetData[0].periodStatus : null;
    const getStatusClass = (status) => {
      switch(status) {
        case 'Open': return 'status-open';
        case 'Approved': return 'status-approved';
        case 'Pending Approval': return 'status-pending';
        case 'Rejected': return 'status-rejected';
        default: return 'status-default';
      }
    };
    
    return (
      <div className="myTimeSheetTopBar">
        <div className="timesheetControlsRow">
          <div className="timesheetLeftControls">
            {periodWeek(true)}
            <div className="timesheetButtonGroup">
              <button
                disabled={
                  isAddButtonClicked ||
                  isEditButtonClicked ||
                  (timeSheetData &&
                    timeSheetData.length &&
                    timeSheetData[0].periodStatus !== "Open")
                }
                className={
                  isAddButtonClicked ||
                  isEditButtonClicked ||
                  (timeSheetData &&
                    timeSheetData.length &&
                    timeSheetData[0].periodStatus !== "Open")
                    ? "disableAddButton"
                    : "addBtn"
                }
                onClick={() => setIsAddButtonClicked(true)}
              >
                Add
              </button>
              <button
                disabled={
                  isAddButtonClicked ||
                  isEditButtonClicked ||
                  (timeSheetData &&
                    timeSheetData.length &&
                    getTimesheetStatus(timeSheetData))
                }
                className={
                  isAddButtonClicked ||
                  isEditButtonClicked ||
                  (timeSheetData &&
                    timeSheetData.length &&
                    getTimesheetStatus(timeSheetData))
                    ? "disableAddButtonSubmit"
                    : "addBtnSubmit"
                }
                onClick={() => {
                  let sum = 0;
                  rows.forEach((row) => {
                    if (row.status !== "Rejected") sum += parseFloat(row.totalHrs);
                  });
                  if (sum.toFixed(2) >= 40) dispatch(submitPeriodForApproval());
                  else {
                    if (
                      timeSheetData &&
                      timeSheetData[0] &&
                      userData.data.joinDate >=
                        timeSheetData[0].dateHours[0].date &&
                      userData.data.joinDate <= timeSheetData[0].dateHours[4].date
                    ) {
                      const daysDifference = differenceInDays(
                        parseISO(timeSheetData[0].dateHours[4].date),
                        parseISO(userData.data.joinDate)
                      );
                      if (sum.toFixed(2) >= (daysDifference + 1) * 8) {
                        dispatch(submitPeriodForApproval());
                      } else
                        toast.info(
                          `Total Hours must be greater than or equal to ${
                            (daysDifference + 1) * 8
                          }`,
                          toastOptions
                        );
                    } else
                      toast.info(
                        "Total Hours must be greater than or equal to 40",
                        toastOptions
                      );
                  }
                }}
              ></button>
            </div>
          </div>
          {periodStatus && (
            <div className="timesheetStatusBadge">
              <span className="statusLabel">Status:</span>
              <span className={`statusValue ${getStatusClass(periodStatus)}`}>
                {periodStatus}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };
  const handleSetColumnsData = (data) => {
    const temp = [];
    let totalHrs = {};
    let status = {};
    if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "TRAINING AND SESSION"
    ) {
      setColumns(tableColumnsData["TrainingAndSession"]);
    } else if (
      headingName !== Modules.TIMESHEET &&
      headingName !== Modules.REPORTING &&
      headingName !== Modules.PROJECT_ALLOCATION
    ) {
      if (
        headingName === Modules.PROJECT_ADMIN &&
        userData.data.roleName === "Project Manager"
      ) {
        setColumns(tableColumnsData["ProjectAdminManager"]);
      } else {
        setColumns(tableColumnsData[headingName.replace(" ", "")]);
      }
    } else if (headingName === Modules.REPORTING) {
      setColumns(tableColumnsData[titleCase(tabName).replace(" ", "")]);
    } else {
      Object.keys(data).forEach((col) => {
        if (
          col === "dateHours" &&
          tabName !== "REPORTEES" &&
          tabName !== "PENDING APPROVAL"
        ) {
          data[col].forEach((dateHour, index) => {
            let month = {
              isDate: true,
            };
            const trimmedDate = dateHour.date.split("T")[0];
            if (index === 0 || moment(trimmedDate).format("DD") === "01")
              month["day"] = moment(trimmedDate).format("MMM");
            temp.push({
              id: moment(trimmedDate).format("ddd DD"),
              label: moment(trimmedDate).format("ddd DD"),
              date: moment(trimmedDate).format(),
              minWidth: 55,
              sortDir: "",
              align: "left",
              ...month,
            });
          });
        } else if (col === "periodWeek" && tabName === "PENDING APPROVAL") {
          temp.push({
            id: col,
            label: getLabel(col, "PendingApproval"),
            minWidth: 250,
            maxWidth: 300,
            sortDir: "DESC",
            align: "left",
            isRequired: col.isRequired,
          });
        } else if (col === "dayHours" && tabName === "PENDING APPROVAL") {
          data[col].forEach((dayHour, index) => {
            temp.push({
              id: dayHour.day,
              label: dayHour.day,
              minWidth: 55,
              sortDir: "",
              align: "left",
            });
          });
        } else if (col === "totalHrs") {
          totalHrs = {
            id: col,
            label: getLabel(col, headingName),
            minWidth: getMinWidth(col, headingName),
            sortDir: "DESC",
            align: "left",
          };
        } else if (
          col === "status" &&
          headingName === Modules.PROJECT_ALLOCATION &&
          tabName === "RESOURCE ALLOCATION"
        ) {
          temp.push({
            id: col,
            label: "Status",
            minWidth: 110,
            align: "left",
          });
        } else if (col === "status" && headingName === Modules.TIMESHEET) {
          if (tabName === "MY TIMESHEET" || tabName === "REPORTEES") {
            status = {
              id: col,
              label: getLabel(col, headingName),
              minWidth: getMinWidth(col, headingName),
              sortDir: "DESC",
              align: "left",
            };
          }
        } else if (!col.includes("Id") && getLabel(col, headingName) !== "") {
          temp.push({
            id: col,
            label: getLabel(col, headingName),
            minWidth: getMinWidth(col, headingName),
            sortDir: "DESC",
            align: "left",
            isRequired: col.isRequired,
          });
        } else if (col === "employeeId" && tabName !== "MY TIMESHEET") {
          temp.push({
            id: "employeeName",
            label:
              tabName === "PENDING APPROVAL" || tabName === "REPORTEES"
                ? "Employee Name"
                : getLabel("employeeName", headingName),
            minWidth:
              tabName === "PENDING APPROVAL" || tabName === "REPORTEES"
                ? "12rem"
                : getMinWidth("employeeName", headingName),
            sortDir: "DESC",
            align: "left",
          });
        }
      });
      if (headingName === Modules.PROJECT_ALLOCATION) {
        if (tabName === "RESOURCE ALLOCATION") {
          temp.push({
            id: "actions",
            label: "View History",
            minWidth: 100,
            sortDir: "",
            align: "left",
          });
        }
        setColumns([...temp]);
      } else {
        if (totalHrs) {
          var removeByAttr = function (arr, attr, value) {
            var i = arr.length;
            while (i--) {
              if (
                arr[i] &&
                arr[i].hasOwnProperty(attr) &&
                arguments.length > 2 &&
                arr[i][attr] === value
              ) {
                arr.splice(i, 1);
              }
            }
            return arr;
          };
          if (tabName === "REPORTEES") {
            removeByAttr(temp, "id", "task");
            removeByAttr(temp, "id", "projectName");
            setColumns([
              ...temp,
              totalHrs,
              status,
              {
                id: "viewDetails",
                label: "View Details",
                minWidth: 80,
                type: "action",
              },
            ]);
          } else {
            setColumns([
              ...temp,
              totalHrs,
              {
                id: "actions",
                label: "Actions",
                minWidth: 60,
                sortDir: "",
                align: "left",
              },
              status,
            ]);
          }
        } else {
          setColumns([
            ...temp,
            {
              id: "actions",
              label: "Actions",
              minWidth: 100,
              sortDir: "",
              align: "left",
            },
          ]);
        }
      }
    }
  };

  const createSearchBox = (placeHolder) => {
    return (
      <div className="searchWrapper">
        <img src={searchIcon} className="searchIcon" alt="" />
        <input
          className="searchBox"
          type="search"
          placeholder={placeHolder}
          ref={inputRef}
          onChange={setSearchDataHelper}
        />
      </div>
    );
  };

  const createTopBarForSessionAndTraining = (placeHolder) => {
    if (userData?.data?.roleName === "Admin") {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div className="searchWrapper">
            <img src={searchIcon} className="searchIcon" alt="" />
            <input
              className="searchBox"
              type="search"
              placeholder={placeHolder}
              ref={inputRef}
              onChange={setSearchDataHelper}
            />
          </div>
          <div style={{ fontSize: "1.5rem", color: "#1773BC" }}>
            <button
              className="addBtn"
              onClick={() => {
                setOpencreateSessionPopup(true);
              }}
            >
              Create Session
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div className="searchWrapper">
            <img src={searchIcon} className="searchIcon" alt="" />
            <input
              className="searchBox"
              type="search"
              placeholder={placeHolder}
              ref={inputRef}
              onChange={setSearchDataHelper}
            />
          </div>
        </div>
      );
    }
  };

  const createSearchBoxforMissingTimesheet = (placeHolder) => {
    return (
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className="searchWrapper">
          <img src={searchIcon} className="searchIcon" alt="" />
          <input
            className="searchBox"
            type="search"
            placeholder={placeHolder}
            ref={inputRef}
            onChange={setSearchDataHelper}
          />
        </div>
        <div
          style={{ fontSize: "1.5rem", marginLeft: "90rem", color: "#1773BC" }}
        >
          Missing Timesheets : {missingReportData}{" "}
        </div>
      </div>
    );
  };

  const setTableData = (tableData) => {
    handleSetColumnsData(tableData.data[0]);
    setTotalRecord(tableData.totalCount);
  };

  const resetSearchData = () => {
    setSearchData("");
    if (inputRef && inputRef.current) inputRef.current.value = "";
  };

  const setSearchDataHelper = (e) => {
    if (headingName === Modules.REPORTING) {
      let value = e.target.value.toLowerCase();
      let searchedData = reportingData.filter((data) => {
        if (
          data.employeeName.toLowerCase().includes(value) ||
          data.periodWeek.toLowerCase().includes(value)
        ) {
          return data;
        }
      });
      setRows(searchedData);
      // setMissingReportData(searchedData.length);
      return;
    }
    if (
      (headingName !== Modules.REPORTING && e.target.value.length > 2) ||
      e.target.value.length === 0
    ) {
      setSearchData(e.target.value);
    }
  };

  const periodWeekClickHandler = (fromMyTimeSheet, periodWeek) => {
    setSelectedPriodWeek(periodWeek);
    if (headingName === Modules.TIMESHEET) {
      if (fromMyTimeSheet) {
        dispatch(
          saveTimeSheetPeriodData({
            periodWeek:
              periodWeek.startDate.format(DATE_FORMAT) +
              " - " +
              periodWeek.endDate.format(DATE_FORMAT),
            startDT: periodWeek.startDate.format(),
            endDT: periodWeek.endDate.endOf("isoweek").format(),
          })
        );
        setRowToBeUpdated({});
        setIsAddButtonClicked(false);
        setIsEditButtonClicked(false);
      }
      let startDate = moment(periodWeek.startDate);
      let endDate = moment(periodWeek.endDate);
      if (tabName === "MY TIMESHEET")
        dispatch(
          getAssignedProjects({
            startDate: moment(startDate).format(DATE_TIME_FORMAT),
            endDate: moment(endDate).format(DATE_TIME_FORMAT),
          })
        );
      dispatch(
        setTimeSheetPeriodWeek(
          periodWeek.startDate.format(DATE_FORMAT) +
            " - " +
            periodWeek.endDate.format(DATE_FORMAT)
        )
      );
    } else if (headingName === Modules.REPORTING) {
      setDisableDate(true);
    }
  };
  const getDateItems = (fromMyTimeSheet) => {
    return periodWeeks.map((periodWeek) => (
      <MenuItem
        value={
          periodWeek.startDate.format(DATE_FORMAT) +
          " - " +
          periodWeek.endDate.format(DATE_FORMAT)
        }
        onClick={() => periodWeekClickHandler(fromMyTimeSheet, periodWeek)}
      >
        {periodWeek.startDate.format(DATE_FORMAT) +
          " - " +
          periodWeek.endDate.format(DATE_FORMAT)}
      </MenuItem>
    ));
  };

  const getTimesheetStatus = (timeSheetData) => {
    for (let i = 0; i < timeSheetData.length; i++) {
      if (timeSheetData[i].status === "Open") return false;
    }
    return true;
  };

  const getEmployeeName = (id) => {
    let employeeName = "";
    if(!id || id === null){
      return null;
    }

    if(allUserDetailsDict){
      let user = allUserDetailsDict[id.toLowerCase()];
      if(user){
        employeeName = getFullName(user.firstName, user.lastName);
        
        // 🔍 Print all details available in allUserDetailsDict for this user
      }
    }

    if(employeeName.length === 0){
      return null;
    }
    return employeeName;
  };

  // 🔍 Helper function to print all users in allUserDetailsDict
  const printAllUserDetailsDict = () => {
    if (!allUserDetailsDict) {
      return;
    }

    
    // Return the dict for further inspection
    return allUserDetailsDict;
  };

  const findBillStatus = (row) => {
    const billStatusFields = [
      "billStatus",
      "Bill Status",
      "BillStatus",
      "Billable Status", 
      "Status",
      "billable_status",
      "bill_status",
      "Type",
      "Resource Type",
      "Category",
    ];
    for (const field of billStatusFields) {
      if (row[field] && String(row[field]).trim() !== "") {
        const value = String(row[field]).trim();
        return value;
      }
    }

    for (const key of Object.keys(row)) {
      const keyLower = key.toLowerCase();
      if (
        keyLower.includes("billable") ||
        keyLower.includes("status") ||
        keyLower.includes("type")
      ) {
        if (row[key] && String(row[key]).trim() !== "") {
          return String(row[key]).trim();
        }
      }
    }


    return "unknown";
  };

  useEffect(() => {
    dispatch(setListItems(null));

    dispatch(
      setTimeSheetPeriodWeek(
        `${selectedPeriodWeek.startDate.format(
          DATE_FORMAT
        )} - ${selectedPeriodWeek.endDate.format(DATE_FORMAT)}`
      )
    );
    if (
      timeSheetData === null &&
      headingName === Modules.TIMESHEET &&
      tabName === "MY TIMESHEET" &&
      queryPeriodWeek === null
    ) {
      dispatch(
        saveTimeSheetPeriodData({
          periodWeek:
            moment().startOf("isoweek").format(DATE_FORMAT) +
            " - " +
            moment().endOf("isoweek").format(DATE_FORMAT),
          startDT: moment().startOf("isoweek").format(),
          endDT: moment().endOf("isoweek").format(),
        })
      );
      dispatch(
        setTimeSheetPeriodWeek(
          moment().startOf("isoweek").format(DATE_FORMAT) +
            " - " +
            moment().endOf("isoweek").format(DATE_FORMAT)
        )
      );
      setRowToBeUpdated({});
      setIsAddButtonClicked(false);
      setIsEditButtonClicked(false);
    }
    if (headingName === Modules.TIMESHEET && tabName === "MY TIMESHEET") {
      dispatch(
        getMyTimeSheetData({
          periodWeek:
            selectedPeriodWeek.startDate.format(DATE_FORMAT) +
            " - " +
            selectedPeriodWeek.endDate.format(DATE_FORMAT),
        })
      );
    } else if (headingName === Modules.TIMESHEET) {
      if (tabName === "REPORTEES") {
        dispatch(
          getTimeSheetReportee({
            periodWeek:
              selectedPeriodWeek.startDate.format(DATE_FORMAT) +
              " - " +
              selectedPeriodWeek.endDate.format(DATE_FORMAT),
            projectId: "",
            employeeId: "",
          })
        );
      } else {
        dispatch(
          getTimeSheetData({
            periodWeek:
              tabName === "PENDING APPROVAL"
                ? "0"
                : selectedPeriodWeek.startDate.format(DATE_FORMAT) +
                  " - " +
                  selectedPeriodWeek.endDate.format(DATE_FORMAT),
            employeeId: null,
            projectId: null,
          })
        );
      }
    }

    if (headingName === Modules.REPORTING) {
      if (tabName === "MISSING TIMESHEET") {
        dispatch(getNotifications());
      } else if (tabName === "TIMESHEET REPORTS") {
        if (selectTimesheetProjectsIds)
          dispatch(
            getTimesheetReports({
              startDate: moment(selectedPeriodWeek.endDate).format(
                DATE_TIME_FORMAT
              ),
              endDate: moment(selectedPeriodWeek.endDate).format(
                DATE_TIME_FORMAT
              ),
              projectId: selectTimesheetProjectsIds,
            })
          );
        dispatch(getMappedProjectManagementData());
        setDisableDate(true);
        setDisablePeriodWeek(false);
      } else if (tabName === "ALLOCATION CHANGE REPORT BY PROJECT") {
        dispatch(setAllocationChangeReportData([]));
      } else if (tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE") {
        dispatch(setAllocationChangeReportByEmployee([]));
      }
    }
    if (
      headingName === Modules.PROJECT_ALLOCATION &&
      tabName === "RESOURCE ALLOCATION"
    ) {
      setRows([]);
    }
    if(headingName === Modules.VIEW_ATTENDANCE){
      dispatch(setViewAttendanceData([]))
    }
    if(headingName === Modules.CORRECT_ATTENDANCE){
      dispatch(setViewAttendanceData([]))
    }
  }, [tabName]);

  useEffect(() => {
    if (headingName === Modules.REPORTING) {
      dispatch(setReportingData(null));
    }
    if(headingName === Modules.TRAINING_AND_SESSION && tabName === 'TRAINING AND SESSION'){
      dispatch(getAllSessionsData())
    }
    if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "SESSION HISTORY"
    ) {
      dispatch(getSessionUserHistory());
    }
    if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "REPORTING DASHBOARD"
    ) {
      // No specific dispatch needed for reporting dashboard
    }
    if (headingName === Modules.TRAINING_AND_SESSION) {
      dispatch(getSessionFeedbackQuestionsList());
    }
  }, [tabName]);

  useEffect(() => {
    if (
      headingName === Modules.CLIENT_ADMIN &&
      clientAdminData &&
      clientAdminData.totalCount
    ) {
      setTableData(clientAdminData);
      setRows(clientAdminData.data);
    } else if (headingName === Modules.SOW) {
      setColumns(tableColumnsData["SOW"]);
      if (sowData && (sowData.totalCount !== undefined || (sowData.data && sowData.data.length >= 0))) {
        setTotalRecord(sowData.totalCount ?? (sowData.data?.length ?? 0));
        setTableData(sowData);
        setRows(sowData.data || []);
      } else {
        setTotalRecord(0);
        setRows([]);
      }
    } else if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "TRAINING AND SESSION" &&
      sessionDetails &&
      sessionDetails.totalCount
    ) {
      setTableData(sessionDetails);
      setRows(sessionDetails.data);
    } else if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "SESSION HISTORY"
    ) {
      if (userData.data.roleName === "Admin")
        setColumns(tableColumnsData["SessionHistoryAdmin"]);
      else
        setColumns(tableColumnsData["SessionHistory"]);
        setRows(sessionUserHistoryData);
    } else if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "REPORTING DASHBOARD"
    ) {
      // For REPORTING DASHBOARD, we'll render the SessionReporting component
      setColumns([]);
      setRows([]);
    }
    else if (
      headingName === Modules.VIEW_ATTENDANCE && 
      viewAttendanceData && 
      viewAttendanceData.length
    ) {
      setColumns(tableColumnsData["ViewAttendance"]);
      setRows(viewAttendanceData);
    }
    else if (
      headingName === Modules.CORRECT_ATTENDANCE
    ) {
      setColumns(tableColumnsData["CorrectAttendance"]);
      // For CORRECT_ATTENDANCE, we start with empty rows as it's a form for adding new data
      setRows([]);
    }
    else if (
      headingName === Modules.PROJECT_ADMIN &&
      projectAdminData &&
      projectAdminData.totalCount
    ) {
      setTableData(projectAdminData);
      // Apply client filter if selected, otherwise show all
      if (selectedClientFilter && selectedClientFilter.name) {
        const filteredData = projectAdminData.data.filter(row => {
          return row.clientName === selectedClientFilter.name;
        });
        setRows(filteredData);
      } else {
        setRows(projectAdminData.data);
      }
    } else if (
      headingName === Modules.PROJECT_ALLOCATION &&
      projectAllocationData &&
      projectAllocationData.totalCount
    ) {
      if (projectAllocationData.data && projectAllocationData.data.length > 0) {
        handleSetColumnsData(projectAllocationData.data[0]);
      }
      setTableData(projectAllocationData);
      setRows(projectAllocationData.data);
    } else if (
      headingName === Modules.TIMESHEET &&
      timeSheetData &&
      timeSheetData.length
    ) {
      handleSetColumnsData(timeSheetData[0]);
      setTotalRecord(timeSheetData.length);
      setRows(handleSetRows(timeSheetData, tabName));
      setIsAddButtonClicked(false);
    } else if (
      headingName === Modules.PROJECT_MANAGEMENT &&
      projectManagementData &&
      projectManagementData.totalCount
    ) {
      setTableData(projectManagementData);
      // Don't set rows here - let the billStatus filtering useEffect handle it
    } else if (
      headingName === Modules.PROJECT_MANAGEMENT &&
      (!projectManagementData || projectManagementData.totalCount === 0)
    ) {
      setColumns(tableColumnsData[headingName.replace(" ", "")]);
      setRows([]);
    } else if (headingName === Modules.REPORTING) {
      if (reportingData && reportingData.length) {
        setColumns(tableColumnsData[titleCase(tabName).replace(" ", "")]);
        setRows(reportingData);
        setMissingReportData(reportingData.length);
      } else {
        setRows([]);
        setMissingReportData(0);
      }
    } else if (
      headingName === Modules.SKILLS &&
      skillsList &&
      skillsList.length
    ) {
      setColumns(tableColumnsData[titleCase(tabName).replace(" ", "")]);
      setRows(skillsList);
      setSkillsLength(skillsList.length);
    } else if (
      headingName === Modules.INVOICE_TRACKER &&
      invoiceTrackerData &&
      invoiceTrackerData.totalCount
    ) {
      setTableData(invoiceTrackerData);
      setRows(invoiceTrackerData.data);
    } else if (
      headingName === Modules.VIEW_ATTENDANCE &&
      viewAttendanceData &&
      viewAttendanceData.length
    ) {
      setColumns(tableColumnsData["ViewAttendance"]);
      setRows(viewAttendanceData);
    } else if (
      headingName === Modules.CORRECT_ATTENDANCE &&
      viewAttendanceData &&
      viewAttendanceData.length
    ) {
      setColumns(tableColumnsData["CorrectAttendance"]);
      setRows(viewAttendanceData);
    } else if(
      headingName === Modules.RESOURCE_ALLOCATION
    ){
      // For RESOURCE_ALLOCATION, data is handled by separate useEffects
      // that process projectAllocationData or allProjectManagementData
      setColumns([]);
      setRows([]);
    }
    else {
      if (tableColumnsData[headingName.replace(" ", "")]) {
        let temp = [...tableColumnsData[headingName.replace(" ", "")]];
        if (headingName === Modules.TIMESHEET && tabName === "MY TIMESHEET") {
          status = temp.pop();
        }
        if (headingName === Modules.TIMESHEET) {
          let totalHrs = temp.pop();
          let date = moment(selectedPeriodWeek.startDate);
          if (tabName !== "REPORTEES" && tabName !== "PENDING APPROVAL") {
            for (let i = 0; i < 7; i++) {
              if (i === 0 || moment(date).format("DD") == "01") {
                let tempDate = moment(date).format();
                let month = {
                  isDate: true,
                };
                month["day"] = moment(tempDate).format("MMM");
                temp.push({
                  id: moment(date).format("ddd DD"),
                  label: moment(date).format("ddd DD"),
                  date: moment(date).format(),
                  minWidth: 55,
                  sortDir: "",
                  align: "left",
                  isDate: true,
                  ...month,
                });
              } else {
                temp.push({
                  id: moment(date).format("ddd DD"),
                  label: moment(date).format("ddd DD"),
                  date: moment(date).format(),
                  minWidth: 55,
                  sortDir: "",
                  align: "left",
                  isDate: true,
                });
              }
              date.add(1, "days");
            }
            temp.push(totalHrs);
          }
          if (tabName === "PENDING APPROVAL") {
            let temp1 = [...tableColumnsData["PendingApproval"]];
            let totalHrs = temp1.pop();
            const weekDay = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            for (let i = 0; i < 7; i++) {
              temp1.push({
                id: weekDay[i],
                label: weekDay[i],
                minWidth: 55,
                sortDir: "",
                align: "left",
              });
            }
            temp1.push(totalHrs);
            setColumns([...temp1]);
          }

          if (headingName === Modules.TIMESHEET) {
            if (tabName !== "PENDING APPROVAL") {
              setColumns([
                ...temp,
                {
                  id: "actions",
                  label: "Actions",
                  minWidth: 100,
                  sortDir: "",
                  align: "left",
                  isRequired: true,
                },
              ]);
            }
          } else {
            setColumns([...temp]);
          }
        } else if (tableColumnsData[titleCase(tabName).replace(" ", "")]) {
          setColumns(tableColumnsData[titleCase(tabName).replace(" ", "")]);
        } else {
          setColumns([]);
        }
        setRows([]);
        setTotalRecord(0);
      }
    }
  }, [
    clientAdminData,
    sowData,
    projectAdminData,
    projectAllocationData,
    projectManagementData,
    timeSheetData,
    reportingData,
    missingReportData,
    invoiceTrackerData,
    headingName,
    skillsList,
    sessionDetails,
    sessionUserHistoryData,
    viewAttendanceData,
  ]);
  // useEffect(()=>{
  //   if(tabName==='TRAINING AND SESSION'&&sessionDetails){
  //     setTableData(sessionDetails);
  //     setRows(sessionDetails.data);
  //     for(let i=0;i<sessionDetails.data.length;i++){
  //       rows.push(sessionDetails.data[i]);
  //     }
  //     // setColumns(sessionDetails);
  //   }
  // },[headingName,tabName]);

  useEffect(() => {
    const currentDate = new Date();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();

    setCurrentMonth(month);
    setCurrentYear(year);
  }, [headingName]);

  useEffect(() => {
    switch (headingName) {
      case Modules.CLIENT_ADMIN:
        dispatch(
          getClientAdminData({
            pageNo: 1,
            pageSize: 10,
            sortBy: "clientName",
            sortDir: "ASC",
            searchData: searchData,
          })
        );
        break;
      case Modules.SOW:
        dispatch(
          getSOWData({
            pageNo: 1,
            pageSize: 10,
            sortBy: "sowName",
            sortDir: "ASC",
            searchData: searchData,
            clientId: sowListClientFilterId,
          })
        );
        break;
      case Modules.PROJECT_ADMIN:
        dispatch(
          getProjectAdminData({
            pageNo: 1,
            pageSize: 200,
            sortBy: "projectName",
            sortDir: "ASC",
            searchData: searchData,
            expiredAllocations: expiredAllocations,
          })
        );
        break;
      case Modules.PROJECT_ALLOCATION:
        if (tabName !== "RESOURCE ALLOCATION") {
          dispatch(
            getProjectAllocationData({
              pageNo: 1,
              pageSize: 10,
              sortBy: "projectName",
              sortDir: "ASC",
              searchData: searchData,
              status: status,
              employeeID: "",
            })
          );
        }
        break;
      case Modules.PROJECT_MANAGEMENT:
        // Allocation list is fetched in the dedicated useEffect below (with projectId in deps) to avoid duplicate API calls on tab switch
        dispatch(getResourcingData({ projectId: projectId }));
        break;
      case Modules.REPORTING:
        if (tabName === "MISSING TIMESHEET") {
          dispatch(
            getMissingTimesheet({
              startDate: moment(moment().startOf("isoweek")).format(
                DATE_TIME_FORMAT
              ),
              endDate: moment(moment().endOf("isoweek")).format(
                DATE_TIME_FORMAT
              ),
              projectId: Array.isArray(selectMissingProjectsIds) ? selectMissingProjectsIds.join(',') : selectMissingProjectsIds,
            })
          );
          dispatch(getMappedProjectManagementData());
          setDisableDate(true);
        }
        break;
      case Modules.TIMESHEET:
        if (tabName !== "MY TIMESHEET") dispatch(getTimesheetProjects());
        break;
      case Modules.INVOICE_TRACKER:
        if (tabName === "INVOICE DETAILS") {
          dispatch(
            getInvoiceTrackerData({
              clientId: selectedClientInvoiceDetail
                ? selectedClientInvoiceDetail.id
                : "",
              projectId: selectedProjectInvoiceDetail
                ? selectedProjectInvoiceDetail.id
                : "",
              startDate: startDateInvoice
                ? startDateInvoice.format(DATE_FORMAT)
                : "",
              endDate: endDateInvoice ? endDateInvoice.format(DATE_FORMAT) : "",
            })
          );
        }
        break;
      case Modules.VIEW_ATTENDANCE:
        if (tabName === "VIEW ATTENDANCE")
          dispatch(
            getViewAttendanceData({
              month: currentMonth + 1,
              year: currentYear,
            })
          );
        break;
      case Modules.TRAINING_AND_SESSION:
        if(tabName === "TRAINING AND SESSION")
        dispatch(getAllSessionsData({searchData: searchData}))
        break;
      case Modules.CORRECT_ATTENDANCE:
        if(tabName === "REGULARIZE ATTENDANCE") {
          dispatch(
            getViewAttendanceData({
              month: currentMonth + 1,
              year: currentYear
            })
          );
          // Load all user details for the employee dropdown in the form
          if (allUserDetails === null) dispatch(getAllUserDetails());
        }
        break;
      default:
        break;
    }
  }, [headingName, searchData, sowListClientFilterId]);

  useEffect(() => {
    if (headingName === Modules.SKILLS) {
      if (listItems && listItems.Streams && listItems.Streams.length) {
        setUserStreams(listItems.Streams);
      }
      if (listItems && listItems.Skills && listItems.Skills.length) {
        setUserSkills(listItems.Skills);
      }
      if (
        listItems &&
        listItems.PriSecSkills &&
        listItems.PriSecSkills.length
      ) {
        setUserPriSecSkills(listItems.PriSecSkills);
      }
    }
  }, [listItems]);

  useEffect(() => {
    setRows([]);
    if (clientsData === null && (headingName === Modules.PROJECT_ADMIN || headingName === Modules.SOW)) {
      dispatch(getClientsData());
    }
    if (
      headingName === Modules.PROJECT_ADMIN ||
      headingName === Modules.CLIENT_ADMIN ||
      headingName === Modules.SKILLS
    ) {
      dispatch(
        getListItems({
          headingName: headingName,
        })
      );
    }
    if (
      headingName === Modules.TRAINING_AND_SESSION &&
      tabName === "TRAINING AND SESSION" &&
      userData.data.roleName === "Admin"
    ) {
      dispatch(
        getListItems({
          headingName: Modules.SKILLS,
        })
      );
    }
    if (
      (headingName === Modules.TIMESHEET && tabName === "MY TIMESHEET") ||
      (headingName === Modules.PROJECT_MANAGEMENT &&
        projectManagementData === null)
    ) {
      let startDate = moment(selectedPeriodWeek.startDate);
      let endDate = moment(selectedPeriodWeek.endDate);
      dispatch(
        getAssignedProjects({
          startDate: moment(startDate).format(DATE_TIME_FORMAT),
          endDate: moment(endDate).format(DATE_TIME_FORMAT),
        })
      );
    }
    if (headingName === Modules.TIMESHEET && tabName !== "MY TIMESHEET") {
      if (selectedProjectId) {
        dispatch(getReportees(selectedProjectId));
      } else {
        dispatch(getReportees(null));
      }
    }
    if (headingName === Modules.INVOICE_TRACKER) {
      dispatch(getMappedProjectManagementData());
    }
    resetSearchData();
    setIsAddButtonClicked(false);
    setIsEditButtonClicked(false);
    if (headingName !== Modules.TIMESHEET) {
      dispatch(setSelectedEmployeeId(null));
      dispatch(setSelectedProjectId(null));
    }

    if (headingName === Modules.PROJECT_MANAGEMENT) {
      dispatch(
        getListItems({
          headingName: Modules.PROJECT_MANAGEMENT,
        })
      );
    }
    if (headingName === Modules.INVOICE_TRACKER) {
      dispatch(
        getListItems({
          headingName: Modules.INVOICE_TRACKER,
        })
      );
    }
    setSelectedPriSecSkills([]);
    setSelectedSkills([]);
    setSelectedStreams([]);
    setSelectedPriodWeek(
      queryPeriodWeek !== null
        ? queryPeriodWeek
        : {
            startDate: moment().startOf("isoweek"),
            endDate: moment().endOf("isoweek"),
          }
    );
  }, [headingName]);

  useEffect(() => {
    if (allTasks === null) dispatch(getProjectTasks());
    if (allUserDetails === null) dispatch(getAllUserDetails());
    // Fetch active users with allocation for Project Management
    if (headingName === Modules.PROJECT_MANAGEMENT && allActiveUsersWithAllocation === null) {
      dispatch(getAllActiveUsersWithAllocation());
    }
  }, [headingName]);

  useEffect(() => {
    if (
      selectedProjectData &&
      headingName === Modules.INVOICE_TRACKER &&
      tabName === "CREATE INVOICE" &&
      isAddButtonClicked
    ) {
      let rowsCount = Math.round(
        moment(selectedProjectData.endDate).diff(
          selectedProjectData.startDate,
          "months",
          true
        )
      );
      let startDate = moment(selectedProjectData.startDate);
      let rowsCountList = [];
      let rows = [];
      let invoicedMonthsList = invoicedMonths?.split(",");
      let invoicedDict = {};
      for (let i = 0; i < invoicedMonthsList.length; i++) {
        invoicedDict[invoicedMonthsList[i]] = true;
      }
      for (let i = 0; i < rowsCount; i++) {
        if (!invoicedDict[moment(startDate).format("MMM")]) {
          let row = {
            clientId: selectedProjectData?.clientId,
            projectId: selectedProjectData?.projectId,
            clientName: selectedProjectData?.clientName,
            projectName: selectedProjectData?.projectName,
            billStartDate: startDate.format(DATE_FORMAT),
            billEndDate: moment
              .min([
                moment(startDate).endOf("months"),
                moment(selectedProjectData.endDate),
              ])
              .format(DATE_FORMAT),
            billMonth: moment(startDate).format("MMM"),
            billAmount: "",
            invoiceStatus: "Created",
          };
          rows.push(row);
          rowsCountList.push({ id: "done" });
        }
        startDate = moment(startDate).startOf("months").add(1, "months");
      }
      setRows(rows);
    }
  }, [selectedProjectData, isAddButtonClicked]);

  useEffect(() => {
    if (skillsList && skillsList.length) {
      setSkillsLength(skillsList.length);
    } else {
      setSkillsLength(0);
    }
  }, [skillsList]);

  useEffect(() => {
    if (mappedProjectManagementData && mappedProjectManagementData.length) {
      const dropdownClients = mappedProjectManagementData.map((data) => {
        return {
          name: data.clientName,
          id: data.clientId,
        };
      });

      setFilterClientDropdownData(dropdownClients);
      setClientsForInvoiceDropdown(dropdownClients);

      const dropdownProjects = mappedProjectManagementData.flatMap((data) =>
        data.projects.map((project) => ({
          name: project.projectName,
          id: project.projectId,
        }))
      );
      setProjectsForInvoiceDropdown(dropdownProjects);
    }
  }, [mappedProjectManagementData]);

  useEffect(() => {
    if (selectedClient) {
      const dropdownProjects = mappedProjectManagementData
        .filter((data) => data.clientId === selectedClient.id)[0]
        .projects?.map((project) => {
          return {
            name: project.projectName,
            id: project.projectId,
          };
        });
      setFilterProjectDropdownData(dropdownProjects);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (mappedProjectManagementData) {
      if (selectedClientInvoiceDetail) {
        const dropdownProjects = mappedProjectManagementData
          .filter((data) => data.clientId === selectedClientInvoiceDetail.id)[0]
          .projects?.map((project) => {
            return {
              name: project.projectName,
              id: project.projectId,
            };
          });
        setProjectsForInvoiceDropdown(dropdownProjects);
      } else {
        const dropdownProjects = mappedProjectManagementData.flatMap((data) =>
          data.projects.map((project) => ({
            name: project.projectName,
            id: project.projectId,
          }))
        );
        setProjectsForInvoiceDropdown(dropdownProjects);
      }
    }
  }, [selectedClientInvoiceDetail]);

  useEffect(() => {
    if (selectedClientProject) {
      dispatch(getProjectById({ projectId: selectedClientProject?.id }));
      dispatch(
        getInvoicedMonths({
          projectId: selectedClientProject?.id,
        })
      );
    }
  }, [selectedClientProject]);

  const createTopBarForInvoice = () => {
    return (
      <div className="invoiceTopBar">
        <div className="invoiceTrackerDropdownBox">
          <label>Please Select Client</label>
          <FormControl style={{ minWidth: 400 }}>
            <InputLabel
              required
              className="select-label"
              id={`label-for-Client`}
            >
              Client Name
            </InputLabel>
            <Select
              id="outlined-select-currency"
              labelId={`label-for-Client`}
              label="Client Name"
              value={selectedClient?.name ? selectedClient.name : ""}
              required
              className="select-input"
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 300 } },
              }}
            >
              {filterClientDropdownData &&
                filterClientDropdownData.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.name}
                    onClick={() => {
                      setSelectedClient(option);
                      setSelectedClientProject(null);
                      // setIsAddButtonClicked(true);
                    }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>

        <div className="invoiceTrackerDropdownBox">
          <label>Please Select Project</label>
          <FormControl style={{ minWidth: 400 }}>
            <InputLabel required id="demo-multiple-checkbox-label">
              Projects
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              label="Projects"
              value={
                selectedClientProject?.name ? selectedClientProject.name : ""
              }
              disabled={selectedClient === null}
              className="select-input"
            >
              {filterProjectDropdownData &&
                filterProjectDropdownData.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.name}
                    onClick={() => {
                      setSelectedClientProject({
                        id: option.id,
                        name: option.name,
                      });
                      setIsAddButtonClicked(false);
                    }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
        <div>{createAddButton("Create Invoice")}</div>
      </div>
    );
  };

  const createTopBarForInvoice_Details = () => {
    return (
      <div className="invoice_DetailsTopBar">
        <div className="invoiceTrackerDropdownBox">
          <label>Select Client</label>
          <FormControl style={{ minWidth: "17.5rem" }}>
            <InputLabel
              required
              className="select-label"
              id={`label-for-Client`}
            >
              Client Name
            </InputLabel>
            <Select
              id="outlined-select-currency"
              labelId={`label-for-Client`}
              label="Client Name"
              value={
                selectedClientInvoiceDetail?.name
                  ? selectedClientInvoiceDetail.name
                  : ""
              }
              required
              className="select-input"
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 300 } },
              }}
            >
              {clientsForInvoiceDropdown &&
                clientsForInvoiceDropdown.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.name}
                    onClick={() => {
                      setSelectedClientInvoiceDetail(option);
                    }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>

        <div className="invoiceTrackerDropdownBox">
          <label>Select Project</label>
          <FormControl style={{ minWidth: "17.5rem" }}>
            <InputLabel required id="demo-multiple-checkbox-label">
              Projects
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              label="Projects"
              value={
                selectedProjectInvoiceDetail?.name
                  ? selectedProjectInvoiceDetail.name
                  : ""
              }
              className="select-input"
            >
              {projectsForInvoiceDropdown &&
                projectsForInvoiceDropdown.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.name}
                    onClick={() => {
                      setSelectedProjectInvoiceDetail({
                        id: option.id,
                        name: option.name,
                      });
                    }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
        <label style={{ marginLeft: "0.5rem", marginTop: "0.85rem" }}>
          Select Period
        </label>
        <div style={{ marginLeft: "2 rem" }}>
          {createStartDateEndDateContainerInvoiceDetails()}
        </div>
        <div className="invoiceDetailsClearAndShowButton">
          {createClearAndShowButtonForInvoiceTracker()}
        </div>
      </div>
    );
  };

  const handleUserChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.findIndex((val) => val === "Select All") > -1) {
      if (selectAll) {
        setSelectedUsers([]);
        setSelectedUserIds([]);
      } else {
        setSelectedUsers(allUserDetails.data);
        let ids = allUserDetails.data.map((user) => {
          return user.id;
        });
        setSelectedUserIds(ids.join(","));
      }
      setSelectAll(!selectAll);
    } else {
      setSelectedUsers(value);
      let ids = value.map((user) => {
        return user.id;
      });
      setSelectedUserIds(ids.join(","));
    }
  };

  const handleUserChangeSkills = (event) => {
    const {
      target: { value },
    } = event;
    if (value.findIndex((val) => val === "Select All") > -1) {
      if (selectAll) {
        setSelectedSkills([]);
        setSelectedSkillsIds("");
      } else {
        setSelectedSkills(listItems.Skills);
        let ids = listItems.Skills?.map((user) => {
          return user.longCodeValue;
        });
        setSelectedSkillsIds(ids.join(","));
      }
      setSelectAll(!selectAll);
    } else {
      setSelectedSkills(value);
      let ids = value.map((user) => {
        return user.longCodeValue;
      });
      setSelectedSkillsIds(ids.join(","));
    }
  };

  const handleUserChangePriSecSkills = (event) => {
    const {
      target: { value },
    } = event;
    if (value.findIndex((val) => val === "Select All") > -1) {
      if (selectAllPriSecSkill) {
        setSelectedPriSecSkills([]);
        setSelectedPriSecSkillsIds("");
      } else {
        setSelectedPriSecSkills(listItems.PriSecSkills);
        let ids = listItems.PriSecSkills?.map((user) => {
          return user.longCodeValue;
        });
        setSelectedPriSecSkillsIds(ids.join(","));
      }
      setSelectAllPriSecSkill(!selectAllPriSecSkill);
    } else {
      setSelectedPriSecSkills(value);
      let ids = value.map((user) => {
        return user.longCodeValue;
      });
      setSelectedPriSecSkillsIds(ids.join(","));
    }
  };
  const handleUserChangeStreams = (event) => {
    const {
      target: { value },
    } = event;
    if (value.findIndex((val) => val === "Select All") > -1) {
      if (selectAllStreams) {
        setSelectedStreams([]);
        setSelectedStreamsIds("");
      } else {
        setSelectedStreams(listItems.Skills);
        let ids = listItems.Skills?.map((user) => {
          return user.longCodeValue;
        });
        setSelectedStreamsIds(ids.join(","));
      }
      setSelectAllStreams(!selectAllStreams);
    } else {
      setSelectedStreams(value);
      let ids = value.map((user) => {
        return user.longCodeValue;
      });
      setSelectedStreamsIds(ids.join(","));
    }
  };
  const getStreamsName = () => {
    let selectedStreamsName = "";
    if (selectedStreams && selectedStreams.length) {
      for (let i = 0; i < selectedStreams.length; i++) {
        selectedStreamsName += `${selectedStreams[i].longCodeValue}`;
        if (i !== selectedStreams.length - 1) selectedStreamsName += ", ";
      }
    }
    return selectedStreamsName;
  };

  const getPriSecSkillsName = () => {
    let selectedPriSecSkillsName = "";
    if (selectedPriSecSkills && selectedPriSecSkills.length) {
      for (let i = 0; i < selectedPriSecSkills.length; i++) {
        selectedPriSecSkillsName += `${selectedPriSecSkills[i].longCodeValue}`;
        if (i !== selectedPriSecSkills.length - 1)
          selectedPriSecSkillsName += ", ";
      }
    }
    return selectedPriSecSkillsName;
  };

  const getSkillsName = () => {
    let selectedSkillsName = "";
    if (selectedSkills && selectedSkills.length) {
      for (let i = 0; i < selectedSkills.length; i++) {
        selectedSkillsName += `${selectedSkills[i].longCodeValue}`;
        if (i !== selectedSkills.length - 1) selectedSkillsName += ", ";
      }
    }
    return selectedSkillsName;
  };

  const getUsersNames = () => {
    let selectedNames = "";
    if (selectedUsers && selectedUsers.length) {
      for (let i = 0; i < selectedUsers.length; i++) {
        if (selectedUsers[i].lastName) {
          selectedNames += `${selectedUsers[i].firstName} ${selectedUsers[i].lastName}`;
        } else {
          selectedNames += selectedUsers[i].firstName;
        }
        if (i !== selectedUsers.length - 1) selectedNames += ", ";
      }
    }
    return selectedNames;
  };

  const handleChangeForHistoryInProjectManagement = (value) => {
    setExpiredAllocations(value === "false");
  };

  const handleChangeForHistoryInProjectAdmin = (value) => {
    setExpiredAllocations(value === "false");
  };

  const handleChangeForHistory = (value) => {
    setIsHistory(value === "false");
  };

  const getUserProjectAllocations = () => {
    dispatch(
      getUsersProjectAllocation({
        employeeIds: selectedUserIds,
        isHistory: isHistory,
      })
    );
  };

  const findAllocation = (row) => {
    const allocationFields = [
      "billAllocation",
      "costAllocation",
      "Allocation",
      "allocation",
      "Allocation %",
      "Alloc",
      "Percent Allocation",
      "Percentage",
      "%",
      "Rate",
      "Utilization",
    ];
    for (const field of allocationFields) {
      if (
        row[field] !== undefined &&
        row[field] !== null &&
        row[field] !== ""
      ) {
        const value = parseFloat(row[field]);
        if (!isNaN(value)) {
          return value > 1 ? value / 100 : value;
        }
      }
    }

    for (const key of Object.keys(row)) {
      const keyLower = key.toLowerCase();
      if (
        keyLower.includes("alloc") ||
        keyLower.includes("percent") ||
        keyLower.includes("%") ||
        keyLower.includes("rate")
      ) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
          const value = parseFloat(row[key]);
          if (!isNaN(value)) {
            return value > 1 ? value / 100 : value;
          }
        }
      }
    }

    return 0.5;
  };

  const findAllowedCount = (row) => {
    // First try to get totalBillableCount from the row if it exists
    const allowedFields = [     
      "totalBillableCount"
    ];
    for (const field of allowedFields) {
      if (
        row[field] !== undefined &&
        row[field] !== null &&
        row[field] !== ""
      ) {
        const value = parseInt(row[field]);
        if (!isNaN(value) && value > 0) {
          return value;
        }
      }
    }
    
    // If totalBillableCount doesn't exist, calculate it from the current data
    // Find the project name for this row
    const currentProjectName = findProjectName(row);
    
    if (currentProjectName && rawData && rawData.length > 0) {
      // Count billable employees for this project
      const billableCount = rawData.filter(dataRow => {
        const projectName = findProjectName(dataRow);
        const billStatus = findBillStatus(dataRow);
        return projectName === currentProjectName && 
               (billStatus?.toLowerCase() === 'billable' || billStatus?.toLowerCase() === 'shadow');
      }).length;
      
      if (billableCount > 0) {
        return billableCount;
      }
    }
    
    return 0;
  };

  const findMaxAllocation = (row) => {
    const maxFields = [
      "Max Allocation",
      "Maximum Allocation",
      "Allocation Limit",
      "Budget Limit",
      "max_allocation",
      "allocation_limit",
    ];
    for (const field of maxFields) {
      if (
        row[field] !== undefined &&
        row[field] !== null &&
        row[field] !== ""
      ) {
        const value = parseFloat(row[field]);
        if (!isNaN(value)) {
          return value > 1 ? value : value * 100;
        }
      }
    }
    return 80;
  };

  const parseDate = (dateValue) => {
    if (!dateValue) return null;

    // Handle Date objects
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    // Handle API date strings (ISO format, common formats)
    if (typeof dateValue === "string") {
      // Try direct parsing first
      let date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date;
      }

      // Try common date formats
      const formats = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
        /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
      ];

      for (const format of formats) {
        if (format.test(dateValue)) {
          date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }
    }

    // Handle Excel date numbers (legacy support)
    if (typeof dateValue === "number") {
      const excelEpoch = new Date(1900, 0, 1);
      const date = new Date(
        excelEpoch.getTime() + (dateValue - 2) * 24 * 60 * 60 * 1000
      );
      return isNaN(date.getTime()) ? null : date;
    }

    // Handle moment.js objects or other date-like objects
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      try {
        return dateValue.toDate();
      } catch (e) {
        console.warn('Failed to convert date object:', dateValue);
      }
    }

    // Handle objects with 't' and 'v' properties (Excel-like format)
    if (dateValue && typeof dateValue === 'object' && dateValue.v) {
      return parseDate(dateValue.v);
    }

    console.warn('Could not parse date value:', dateValue, typeof dateValue);
    return null;
  };

  const findStartDate = (row) => {
    const startDateFields = [
      "startDate",
      "Start Date",
      "StartDate",
      "Allocation Start",
      "Start",
      "start_date",
      "allocation_start",
      "Project Start Date",
    ];
    for (const field of startDateFields) {
      if (row[field]) {
        const dateValue = parseDate(row[field]);
        if (dateValue) return dateValue;
      }
    }
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - 30);
    return defaultDate;
  };

  const findEndDate = (row) => {
    const endDateFields = [
      "endDate",
      "End Date",
      "EndDate",
      "Allocation End",
      "End",
      "end_date",
      "allocation_end",
      "Project End Date",
    ];
    for (const field of endDateFields) {
      if (row[field]) {
        const dateValue = parseDate(row[field]);
        if (dateValue) {
          return dateValue;
        }
      }
    }


    // Generate realistic test dates for demo purposes
    const generateRandomEndDate = () => {
      const today = new Date();
      const randomDays = Math.random();
      
      if (randomDays < 0.1) {
        // 10% expired (past dates)
        const expiredDate = new Date(today);
        expiredDate.setDate(expiredDate.getDate() - Math.floor(Math.random() * 30) - 1);
        return expiredDate;
      } else if (randomDays < 0.2) {
        // 10% expiring in next 10 days
        const soonDate = new Date(today);
        soonDate.setDate(soonDate.getDate() + Math.floor(Math.random() * 10) + 1);
        return soonDate;
      } else if (randomDays < 0.3) {
        // 10% expiring in next 20 days
        const mediumDate = new Date(today);
        mediumDate.setDate(mediumDate.getDate() + Math.floor(Math.random() * 10) + 11);
        return mediumDate;
      } else if (randomDays < 0.4) {
        // 10% expiring in next 30 days
        const laterDate = new Date(today);
        laterDate.setDate(laterDate.getDate() + Math.floor(Math.random() * 10) + 21);
        return laterDate;
      } else {
        // 60% far future dates (not expiring soon)
        const futureDate = new Date(today);
        futureDate.setDate(futureDate.getDate() + Math.floor(Math.random() * 365) + 31);
        return futureDate;
      }
    };

    return generateRandomEndDate();
  };

  const processDataForCharts = useCallback((dataToProcess) => {

    if (!dataToProcess || dataToProcess.length === 0) {
      setResourceCountData([]);
      setBillStatusSummary([]);
      setProjectSummary([]);
      return;
    }

    // Filter out rows that don't have the specific bill statuses we want to show
    const filteredData = dataToProcess.filter((row) => {
      const billStatus = findBillStatus(row);
      const statusLower = billStatus.toLowerCase();
      
      // Allow billable, shadow, and any non-billable variants (including NB- prefixed)
      return statusLower === 'billable' || 
             statusLower === 'shadow' || 
             statusLower === 'non-billable' || 
             statusLower === 'non billable' || 
             statusLower.startsWith('nb-') || 
             statusLower.startsWith('nb ');
    });


    const countGroups = {};
    const allocationGroups = {};
    const projectMetadata = {};

    filteredData.forEach((row, index) => {
      const projectName = findProjectName(row);
      const billStatus = findBillStatus(row);
      
      // Normalize bill status before using as key
      let normalizedBillStatus = billStatus;
      const statusLower = billStatus.toLowerCase();
      if (statusLower === 'non-billable' || statusLower === 'nonbillable' || statusLower.startsWith('nb-') || statusLower.startsWith('nb ')) {
        normalizedBillStatus = 'Non Billable';
      } else if (statusLower === 'billable') {
        normalizedBillStatus = 'Billable';
      } else if (statusLower === 'shadow') {
        normalizedBillStatus = 'Shadow';
      }
      
      const allocation = findAllocation(row);
      const allowedCount = findAllowedCount(row);
      const maxAllocation = findMaxAllocation(row);


      if (!countGroups[projectName]) {
        countGroups[projectName] = {};
        allocationGroups[projectName] = {};
        projectMetadata[projectName] = {
          allowedCount: allowedCount,
          maxAllocation: maxAllocation,
        };
      }

      countGroups[projectName][normalizedBillStatus] =
        (countGroups[projectName][normalizedBillStatus] || 0) + 1;
      allocationGroups[projectName][normalizedBillStatus] =
        (allocationGroups[projectName][normalizedBillStatus] || 0) + allocation;
    });

    // const projectDeviations = [];

    // Object.keys(countGroups).forEach((projectName) => {
    //   const projectCounts = countGroups[projectName];
    //   const projectAllocations = allocationGroups[projectName];
    //   const metadata = projectMetadata[projectName];

    //   const currentBillableCount =
    //     (projectCounts["Billable"] || 0) + (projectCounts["billable"] || 0);
    //   const allowedCount = metadata.allowedCount;
    //   const maxAllocation = metadata.maxAllocation;

    //   const currentTotalAllocation =
    //     ((projectAllocations["Billable"] || 0) +
    //       (projectAllocations["billable"] || 0)) *
    //     100;

    //   let deviationType = "normal";
    //   let deviationMessage = "";

    //   const countDeviation =
    //     allowedCount > 0
    //       ? ((currentBillableCount - allowedCount) / allowedCount) * 100
    //       : 0;
    //   const allocationDeviation =
    //     maxAllocation > 0 ? currentTotalAllocation - maxAllocation : 0;

    //   if (countDeviation > 20 || allocationDeviation > 10) {
    //     deviationType = "critical";
    //     const countMsg =
    //       countDeviation > 20 ? `Count +${countDeviation.toFixed(1)}%` : "";
    //     const allocMsg =
    //       allocationDeviation > 10
    //         ? `Allocation +${allocationDeviation.toFixed(1)}%`
    //         : "";
    //     deviationMessage = `Exceeds limits: ${[countMsg, allocMsg]
    //       .filter(Boolean)
    //       .join(", ")}`;
    //   } else if (countDeviation > 10 || allocationDeviation > 5) {
    //     deviationType = "warning";
    //     const countMsg =
    //       countDeviation > 10 ? `Count +${countDeviation.toFixed(1)}%` : "";
    //     const allocMsg =
    //       allocationDeviation > 5
    //         ? `Allocation +${allocationDeviation.toFixed(1)}%`
    //         : "";
    //     deviationMessage = `Above target: ${[countMsg, allocMsg]
    //       .filter(Boolean)
    //       .join(", ")}`;
    //   }

    //   if (deviationType !== "normal") {
    //     projectDeviations.push({
    //       project: projectName,
    //       currentBillableCount,
    //       allowedCount,
    //       currentTotalAllocation,
    //       maxAllocation,
    //       countDeviation,
    //       allocationDeviation,
    //       deviationType,
    //       deviationMessage,
    //       totalResources: Object.values(projectCounts).reduce(
    //         (sum, count) => sum + count,
    //         0
    //       ),
    //       billablePercentage:
    //         Object.values(projectCounts).reduce(
    //           (sum, count) => sum + count,
    //           0
    //         ) > 0
    //           ? (
    //               (currentBillableCount /
    //                 Object.values(projectCounts).reduce(
    //                   (sum, count) => sum + count,
    //                   0
    //                 )) *
    //               100
    //             ).toFixed(1)
    //           : 0,
    //     });
    //   }
    // });


    const countChartData = Object.keys(countGroups).map((projectName) => {
      const projectData = { project: projectName };
      const projectCounts = countGroups[projectName];


      // Normalize bill status keys for consistent chart display
      let billableCount = 0;
      let nonBillableCount = 0;
      let shadowCount = 0;

      Object.keys(projectCounts).forEach((billStatus) => {
        const count = projectCounts[billStatus];
        
        if (billStatus === 'Billable') {
          billableCount += count;
        } else if (billStatus === 'Non Billable') {
          nonBillableCount += count;
        } else if (billStatus === 'Shadow') {
          shadowCount += count;
        } else {
          // Handle any other statuses - group as non-billable for chart purposes
          nonBillableCount += count;
        }
      });

      // Set standardized keys for chart consumption
      projectData.Billable = billableCount;
      projectData['Non-Billable'] = nonBillableCount;
      projectData.Shadow = shadowCount;

      const totalResources = billableCount + nonBillableCount + shadowCount;
      projectData.billablePercentage =
        totalResources > 0
          ? Math.round((billableCount / totalResources) * 100)
          : 0;
      projectData.totalResources = totalResources;
      
      // Add totalBillableCount from metadata (allowed count)
      projectData.totalBillableCount = projectMetadata[projectName]?.allowedCount || 0;


      return projectData;
    });


    setResourceCountData(countChartData);
    // setDeviationData(projectDeviations);

    const billStatusTotals = {};
    const billStatusAllocTotals = {};

    filteredData.forEach((row) => {
      const billStatus = findBillStatus(row);
      
      // Normalize bill status for summary as well
      let normalizedBillStatus = billStatus;
      const statusLower = billStatus.toLowerCase();
      if (statusLower === 'non-billable' || statusLower === 'nonbillable' || statusLower.startsWith('nb-') || statusLower.startsWith('nb ')) {
        normalizedBillStatus = 'Non Billable';
      } else if (statusLower === 'billable') {
        normalizedBillStatus = 'Billable';
      } else if (statusLower === 'shadow') {
        normalizedBillStatus = 'Shadow';
      }
      
      const allocation = findAllocation(row);

      billStatusTotals[normalizedBillStatus] = (billStatusTotals[normalizedBillStatus] || 0) + 1;
      billStatusAllocTotals[normalizedBillStatus] =
        (billStatusAllocTotals[normalizedBillStatus] || 0) + allocation;
    });


    const summaryData = Object.keys(billStatusTotals)
      .map((status) => ({
        name: status,
        count: billStatusTotals[status],
        totalAllocation: Math.round(billStatusAllocTotals[status] * 100) / 100,
      }));

    setBillStatusSummary(summaryData);

    const projectSummaryData = Object.keys(countGroups).map((projectName) => {
      const counts = countGroups[projectName];
      const allocations = allocationGroups[projectName];
      const metadata = projectMetadata[projectName];

      const billableCount =
        (counts["Billable"] || 0) + (counts["billable"] || 0);
      const totalCount = Object.values(counts).reduce(
        (sum, val) => sum + val,
        0
      );
      const billableRatio =
        totalCount > 0 ? ((billableCount / totalCount) * 100).toFixed(1) : 0;

      const allowedCount = metadata.allowedCount;
      const maxAllocation = metadata.maxAllocation;

      const countDeviation =
        allowedCount > 0
          ? ((billableCount - allowedCount) / allowedCount) * 100
          : 0;
      const currentTotalAllocation =
        ((allocations["Billable"] || 0) + (allocations["billable"] || 0)) * 100;
      const allocationDeviation =
        maxAllocation > 0 ? currentTotalAllocation - maxAllocation : 0;

      let status = "normal";
      if (countDeviation > 20 || allocationDeviation > 10) {
        status = "critical";
      } else if (countDeviation > 10 || allocationDeviation > 5) {
        status = "warning";
      }

      return {
        project: projectName,
        billableCount,
        allowedCount,
        countDeviation: countDeviation.toFixed(1),
        nonBillableCount:
          (counts["Non-Billable"] || 0) + (counts["non-billable"] || 0),
        shadowCount: (counts["Shadow"] || 0) + (counts["shadow"] || 0),
        totalCount,
        billableRatio,
        currentAllocation: currentTotalAllocation.toFixed(1),
        maxAllocation: maxAllocation.toFixed(1),
        allocationDeviation: allocationDeviation.toFixed(1),
        status,
      };
    });

    setProjectSummary(projectSummaryData);
  }, []);

  const findResourceName = (row) => {
    const nameFields = [
      "employeeName",
      "Resource Name",
      "Name",
      "Employee Name",
      "Full Name",
      "Employee",
      "Resource",
    ];
    for (const field of nameFields) {
      if (row[field]) return String(row[field]).trim();
    }
    return "Unknown Resource";
  };

  const processExpiringAllocations = useCallback((dataToProcess) => {

    const today = new Date();
    
    const expiring = {
      next10days: [],
      next20days: [],
      next30days: [],
      expired: [],
    };
    
    let processedCount = 0;
    let validDateCount = 0;
    
    dataToProcess.forEach((row, index) => {
      const endDate = findEndDate(row);
      
      if (index < 5) {
      }
      
      if (endDate && endDate instanceof Date && !isNaN(endDate)) {
        validDateCount++;
        const daysUntilExpiry = Math.ceil(
          (endDate - today) / (1000 * 60 * 60 * 24)
        );

        // Extract employee ID and GUID for employee identification
        const employeeId = row["Employee ID"] || row["zohoEmployeeId"] || "N/A";
        
        // Try to get the GUID employee ID - check multiple possible field names
        let employeeGuid = row["employeeId"] || row["EmployeeId"] || row["employee_id"] || 
                         row["guid"] || row["GUID"] || row["id"] || null;
        
        // If no GUID found in row, try to map using Employee ID to find the Azure GUID
        if (!employeeGuid && allUserDetailsDict) {
          const resourceName = findResourceName(row);
          
          // Get the Employee ID (Zoho Employee ID) from the row data
          const employeeIdFromRow = row["Employee ID"] || row["zohoEmployeeId"] || row["employeeNumber"] || row["empId"];
          
          
          // Priority 1: Try Employee ID mapping (most reliable)
          if (employeeIdFromRow) {
            const user = Object.values(allUserDetailsDict).find(user => 
              user.employeeId === String(employeeIdFromRow) || 
              user.employeeId === employeeIdFromRow
            );
            
            if (user) {
              employeeGuid = user.id; // This is the Azure GUID we need
            } else {
            }
          }
          
          // Priority 2: Fallback to email matching if Employee ID fails
          
          
        }

        const resourceInfo = {
          name: findResourceName(row),
          project: findProjectName(row),
          billStatus: findBillStatus(row),
          allocation: findAllocation(row),
          endDate: endDate,
          daysUntilExpiry: daysUntilExpiry,
          startDate: findStartDate(row),
          employeeId: employeeId, // Add employee ID for modal
          employeeGuid: employeeGuid, // Add employee GUID for API calls
        };


        if (daysUntilExpiry < 0) {
          expiring.expired.push(resourceInfo);
        } else if (daysUntilExpiry <= 10) {
          expiring.next10days.push(resourceInfo);
        } else if (daysUntilExpiry <= 20) {
          expiring.next20days.push(resourceInfo);
        } else if (daysUntilExpiry <= 30) {
          expiring.next30days.push(resourceInfo);
        }
      }
      processedCount++;
    });

    Object.keys(expiring).forEach((key) => {
      expiring[key].sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
    });


    setExpiringResources(expiring);
  }, []);

  const findStream = (row) => {
    const streamFields = [
      "stream",
      "Stream",
      "department",
      "Department",
      "Team",
      "Practice",
      "Business Unit",
      "Division",
      "Group",
      "Vertical",
      "Workstream",
    ];
    for (const field of streamFields) {
      if (row[field] && String(row[field]).trim() !== "") {
        return String(row[field]).trim();
      }
    }
    return "Unknown Stream";
  };
  const isBenchProject = (row) => {
    const projectName = findProjectName(row);
    const billStatus = findBillStatus(row);

    const projectLower = projectName.toLowerCase();
    const statusLower = billStatus.toLowerCase();

    const isBench =
      projectLower.includes("bench") ||
      projectLower.includes("unallocated") ||
      projectLower.includes("available") ||
      projectLower.includes("free") ||
      statusLower.includes("bench") ||
      statusLower.includes("unallocated") ||
      statusLower.includes("available") ||
      projectLower === "unknown project" ||
      projectLower === "" ||
      projectLower === "n/a" ||
      projectLower === "na" ||
      projectLower === "tbd" ||
      projectLower === "to be decided";

    return isBench;
  };

  // Generate realistic experience data for demo purposes
  const generateRandomExperience = () => {
    // Generate experience between 0-15 years (0-180 months)
    const minMonths = 0;
    const maxMonths = 180;
    const experience = Math.floor(Math.random() * (maxMonths - minMonths + 1)) + minMonths;
    return experience;
  };

  const processBenchResources = useCallback((dataToProcess) => {

    const benchData = dataToProcess.filter((row) => isBenchProject(row));


    if (benchData.length === 0) {
      const alternativeBenchData = dataToProcess.filter((row) => {
        const billStatus = findBillStatus(row);
        const statusLower = billStatus.toLowerCase();
        return statusLower === "shadow" || 
               statusLower === "non-billable" || 
               statusLower === "nonbillable" ||
               statusLower.startsWith('nb-') || 
               statusLower.startsWith('nb ');
      });


      if (alternativeBenchData.length > 0) {
        processBenchData(alternativeBenchData);
        return;
      }

      const fallbackData = dataToProcess.slice(0, 10);
      if (fallbackData.length > 0) {
        processBenchData(fallbackData);
        return;
      }
    }

    processBenchData(benchData);

    function processBenchData(benchDataToProcess) {
      const streamGroups = {};
      const streamSummary = [];
      const allProcessedResources = [];


      // Check for potential duplicates by employee ID
      const employeeIds = new Set();
      const duplicateCheck = new Map();

      benchDataToProcess.forEach((row, index) => {
        const stream = findStream(row);
        const resourceName = findResourceName(row);
        const billStatus = findBillStatus(row);
        const allocation = findAllocation(row);
        const startDate = findStartDate(row);
        const endDate = findEndDate(row);
        const employeeId = row["Employee ID"] || row["zohoEmployeeId"] || "N/A";
        
        // Try to get the GUID employee ID - check multiple possible field names
        let employeeGuid = row["employeeId"] || null;
        
        // If no GUID found in row, try to map using allUserDetails with improved priority-based matching
        if (!employeeGuid && allUserDetailsDict) {
          
          // Priority 1: Try to find by employee ID fields (most reliable)
          const searchEmployeeId = row["Employee ID"] || row["zohoEmployeeId"] || row["employeeNumber"] || row["empId"];
          if (searchEmployeeId) {
            const user = Object.values(allUserDetailsDict).find(user => 
              // The employeeId in allUserDetailsDict is actually the Zoho Employee ID
              user.employeeId === searchEmployeeId || 
              user.employeeId === String(searchEmployeeId) || // Handle string/number conversion
              user.zohoEmployeeId === searchEmployeeId ||
              user.employeeNumber === searchEmployeeId ||
              user.empId === searchEmployeeId
            );
            if (user) {
              employeeGuid = user.id;
            } else {
            }
          } else {
          }
        }

        // Track potential duplicates
        const employeeKey = `${employeeId}-${resourceName}`;
        if (duplicateCheck.has(employeeKey)) {
          console.warn(`⚠️ Potential duplicate detected:`, {
            employeeId,
            name: resourceName,
            stream,
            existingEntry: duplicateCheck.get(employeeKey),
            currentIndex: index
          });
        }
        duplicateCheck.set(employeeKey, { stream, index, name: resourceName });

        const processedResource = {
          name: resourceName,
          billStatus: billStatus,
          allocation: allocation,
          startDate: startDate,
          endDate: endDate,
          stream: stream,
          project: findProjectName(row),
          employeeId: employeeId,
          employeeGuid: employeeGuid || row["employeeId"], // Add GUID employee ID for API calls
          projectAllocationId: row.projectAllocationId || row["projectAllocationId"] || 0, // Use projectAllocationId directly from API response
          jobTitle: row["Job Title"] || row["jobTitle"] || "N/A",
          designation: row["Designation"] || row["designation"] || "N/A",
          skillBucket: row["Skill Bucket"] || row["skillBucket"] || "N/A",
          primarySkill: row["Primary Skill"] || row["primarySkill"] || "N/A",
          secondarySkill: row["Secondary Skill"] || row["secondarySkill"] || "N/A",
          totalExperience: row["totalExperience"] || generateRandomExperience(),
          lastProject: row["lastProject"] || "N/A",
          lastAllocationEndDate: row["lastAllocationEndDate"] || row["LastAllocationEndDate"] || row["last_allocation_end_date"] || row["lastallocationenddate"] || row["endDate"] || null
        };


        // Add to stream groups
        if (!streamGroups[stream]) {
          streamGroups[stream] = [];
        }
        streamGroups[stream].push(processedResource);

        // Add to overall processed resources list
        allProcessedResources.push(processedResource);
        employeeIds.add(employeeId);
      });


      // If duplicates detected, deduplicate by keeping the first occurrence of each employee
      if (benchDataToProcess.length !== employeeIds.size) {
        const seenEmployees = new Set();
        const deduplicatedResources = [];
        const deduplicatedStreamGroups = {};

        allProcessedResources.forEach(resource => {
          const employeeKey = `${resource.employeeId}-${resource.name}`;
          if (!seenEmployees.has(employeeKey)) {
            seenEmployees.add(employeeKey);
            deduplicatedResources.push(resource);

            // Rebuild stream groups with deduplicated data
            if (!deduplicatedStreamGroups[resource.stream]) {
              deduplicatedStreamGroups[resource.stream] = [];
            }
            deduplicatedStreamGroups[resource.stream].push(resource);
          }
        });

        // Replace with deduplicated data
        allProcessedResources.length = 0;
        allProcessedResources.push(...deduplicatedResources);
        
        // Clear and rebuild stream groups
        Object.keys(streamGroups).forEach(key => delete streamGroups[key]);
        Object.assign(streamGroups, deduplicatedStreamGroups);

      }

      // Clear streamSummary to rebuild it correctly
      streamSummary.length = 0;

      Object.keys(streamGroups).forEach((stream) => {
        const resources = streamGroups[stream];
        const totalCount = resources.length;


        streamSummary.push({
          stream: stream,
          totalCount: totalCount,
          resources: resources,
        });
      });

      const totalCountsSum = streamSummary.reduce((sum, s) => sum + s.totalCount, 0);
      const isCountMismatch = totalCountsSum !== allProcessedResources.length;

      // Count verification
      if (isCountMismatch) {

        // Verify each resource is in exactly one stream
        const resourcesInStreams = [];
        Object.values(streamGroups).forEach(resources => {
          resources.forEach(resource => resourcesInStreams.push(resource));
        });
        
      }

      // Set the processed resources instead of raw data
      setBenchResources(allProcessedResources);
      setBenchStreamData(streamSummary);
    }
  }, []);

  useEffect(() => {
    if (allUserDetails && allUserDetails.length) {
      setUserMembers({ ...allUserDetails, data: allUserDetails.data });
    }
  }, [allUserDetails]);

  const findProjectName = (row) => {
    const projectFields = [
      "projectName",
      "Project Name", 
      "ProjectName",
      "Project",
      "project",
      "Project_Name",
    ];
    for (const field of projectFields) {
      if (row[field]) return String(row[field]).trim();
    }
    return "Unknown Project";
  };

  const findProjectAllocationId = (row) => {
    // Check for projectAllocationId in the actual API response
    if (row["projectAllocationId"] !== undefined && row["projectAllocationId"] !== null && row["projectAllocationId"] !== "") {
      return Number(row["projectAllocationId"]);
    }
    
    // Also check alternative field names just in case
    const allocationIdFields = [
      "ProjectAllocationId", 
      "project_allocation_id",
      "allocationId",
      "AllocationId",
      "id"
    ];
    
    for (const field of allocationIdFields) {
      if (row[field] !== undefined && row[field] !== null && row[field] !== "") {
        return Number(row[field]);
      }
    }
    
    // Log what's actually in the row for debugging
    return 0; // Return 0 instead of null as requested
  };

  const processMonthlyProjections = useCallback(async () => {
    let data = [];
    try {
      const response = await getMonthlyProjections(projectId);
      if (!response) {
        throw new Error("Invalid API response format");
      }
      else{
        data=response.filter(item => item.year === "2025");
      }
    } catch (error) {
      console.error("Error fetching monthly projections:");
    }
    const chartData = data.map(item => ({
      month: item.month,
      billableCount: item.billableResourceCount,
      nonBillableCount: item.nonBillableResourceCount,
      shadowCount: item.shadowResourceCount,
     
      billableAllocation: item.billableResourceCount * 7.5,
      nonBillableAllocation: item.nonBillableResourceCount * 5.0,
      shadowAllocation: item.shadowResourceCount * 2.5,
     
      nonBillableDeviation: item.nonBillableResourceCount - item.billableResourceCount,
      shadowDeviation: item.shadowResourceCount - item.billableResourceCount,
     
      nonBillableDeviationPercent:
        item.billableResourceCount > 0
          ? (((item.nonBillableResourceCount - item.billableResourceCount) / item.billableResourceCount) * 100).toFixed(1)
          : 0,
      shadowDeviationPercent:
        item.billableResourceCount > 0
          ? (((item.shadowResourceCount - item.billableResourceCount) / item.billableResourceCount) * 100).toFixed(1)
          : 0,
    }));
    setMonthlyProjections(chartData);
  }, []);

  const createTopBarForProjectAllocation = () => {
    return (
      <>
        <div className="allocationTopBar">
          <div>
            <div className="dropdownsBox">
              <label>Select Resources for Allocations</label>
              <FormControl sx={{ m: 1, width: 300 }}>
                <InputLabel required id="demo-multiple-checkbox-label">
                  Select User
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedUsers}
                  onChange={handleUserChange}
                  input={<OutlinedInput label="Select User" />}
                  renderValue={getUsersNames}
                  MenuProps={MenuProps}
                >
                  {allUserDetails && (
                    <ListSubheader className="subheader">
                      <TextField
                        placeholder="Search Here..."
                        className="subheader-field"
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        autoFocus={true}
                        onChange={(e) => {
                          setUserMembers({
                            ...allUserDetails,
                            data: allUserDetails.data.filter(
                              (i) =>
                                i.firstName
                                  ?.toLowerCase()
                                  .includes(e.target.value.toLowerCase()) ||
                                i.lastName
                                  ?.toLowerCase()
                                  .includes(e.target.value.toLowerCase()) ||
                                i.email
                                  ?.toLowerCase()
                                  .includes(e.target.value.toLowerCase())
                            ),
                          });
                        }}
                      />
                    </ListSubheader>
                  )}
                  <div
                    disabled={!selectedUserIds.length}
                    style={{
                      textAlign: "right",
                      cursor: "pointer",
                      height: "2rem",
                    }}
                  >
                    <Link
                      onClick={() => {
                        setSelectedUsers([]);
                        setSelectedUserIds([]);
                        setSelectAll(false);
                      }}
                      style={{ color: "#1773bc", paddingRight: "1rem" }}
                    >
                      Clear Selection
                    </Link>
                  </div>
                  <MenuItem
                    key={`select-all`}
                    value={"Select All"}
                    className="no-left-margin"
                  >
                    <Checkbox checked={selectAll} />
                    <ListItemText
                      primary={selectAll ? "Select None" : "Select All"}
                    />
                  </MenuItem>
                  {userMembers &&
                    userMembers.data.map((user) => (
                      <MenuItem
                        key={user.id}
                        value={user}
                        className="no-left-margin"
                      >
                        <Checkbox
                          checked={
                            selectedUsers.findIndex(
                              (person) => person.id === user.id
                            ) > -1
                          }
                        />
                        <ListItemText
                          primary={
                            getFullName(user.firstName, user.lastName) +
                            " (" +
                            user.email +
                            ")"
                          }
                        />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>

            <div>
              <label>Include Expired Allocation</label>
              <Switch
                checked={isHistory}
                onChange={(e) => handleChangeForHistory(e.target.value)}
                value={isHistory}
                inputProps={{ "aria-label": "controlled" }}
                id="history"
              />
            </div>
            <div>
              <button
                className={
                  !selectedUserIds || selectedUserIds.length === 0
                    ? "disableAddButton"
                    : "addBtn"
                }
                disabled={!selectedUserIds || selectedUserIds.length === 0}
                onClick={() => getUserProjectAllocations()}
              >
                Get Project Allocations
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const createClearAndViewButtonForAttendance = () => {
    return (
      <>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
        >
          <button
            style={{
              padding: "0.875rem 2.375rem",
              background: "#1773bc",
              border: "0.0625rem solid #1773bc",
              borderRadius: "0.625rem",
              fontFamily: "Montserrat",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: "1.25rem",
              color: "#ffffff",
              cursor: selectedUser ? "pointer" : "not-allowed",
            }}
            className={
              disableDate || disablePeriodWeek
                ? "addBtn showDataBtn"
                : "disableAddButton"
            }
            onClick={() => {
              setSelectedUserID(null);
              setSelectedUser(null);
              setViewAttendanceClicked(false);
              setUserMembers(allUserDetails);
            }}
          >
            Clear Selection
          </button>
        </div>
      </>
    );
  };

  const handleUserChangeForAttendanceDropDown = (event) => {
    const user = event.target.value;
    setSelectedUser(user);
    setSelectedUserID(user.id);
    setUserMembers(allUserDetails);
    setViewAttendanceClicked(true);
  };

  const createTopBarForViewAttendance = () => {
    // Filter employees for attendance based on search term
    const filteredAttendanceEmployees = userMembers?.data?.filter((employee) => {
      if (!attendanceSearchTerm) return true;
      const searchLower = attendanceSearchTerm.toLowerCase();
      const name = getFullName(employee.firstName, employee.lastName).toLowerCase();
      const email = employee.email?.toLowerCase() || '';
      const employeeId = employee.employeeId?.toLowerCase() || '';
      return name.includes(searchLower) || email.includes(searchLower) || employeeId.includes(searchLower);
    }) || [];

    return (
      <>
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "grey",
                marginRight: "1rem",
                whiteSpace: "nowrap",
                fontFamily: "Montserrat",
              }}
            >
              Select User to View Attendance :{" "}
            </span>
            <Autocomplete
              fullWidth
              options={filteredAttendanceEmployees}
              getOptionLabel={(option) => {
                if (!option) return '';
                // Handle both employee dropdown format and allUserDetails format
                const name = option.name || getFullName(option.firstName, option.lastName);
                const employeeId = option.employeeId || '';
                const email = option.email || '';
                // Display format: "Name (EmployeeID) - Email" 
                return employeeId ? `${name} (${employeeId}) - ${email}` : `${name} - ${email}`;
              }}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
                // Handle both data formats for ID (azureId for employee dropdown, id for allUserDetails)
                const userId = newValue ? (newValue.azureId || newValue.id) : null;
                setSelectedUserID(userId);
                setViewAttendanceClicked(!!newValue);
                setUserMembers(allUserDetails);
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setAttendanceSearchTerm(newInputValue);
                } else if (reason === 'clear') {
                  setAttendanceSearchTerm('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search employees by name, ID, or email..."
                  variant="outlined"
                  sx={{
                    width: '300px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px'
                    },
                    '& .MuiInputBase-root': { 
                      minHeight: '40px',
                      height: '40px'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div style={{ width: '100%' }}>
                    <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>
                      {option.name || getFullName(option.firstName, option.lastName)} 
                      {option.employeeId && (
                        <span style={{ color: '#666', fontWeight: 400 }}>
                          {' '}({option.employeeId})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.825rem', color: '#666' }}>
                      {option.email}
                    </div>
                  </div>
                </li>
              )}
              ListboxProps={{
                sx: { maxHeight: 200 }
              }}
              noOptionsText={attendanceSearchTerm ? "No employees found matching your search" : "Start typing to search employees"}
              isOptionEqualToValue={(option, value) => {
                // Handle both data formats for comparison
                const optionId = option?.azureId || option?.id;
                const valueId = value?.azureId || value?.id;
                return optionId === valueId;
              }}
              freeSolo={false}
              clearOnEscape
              openOnFocus
              sx={{ width: '300px', m: 1 }}
            />
            <div>{createClearAndViewButtonForAttendance()}</div>
          </div>
          <a
            onClick={(e) => setOpenDialogBox(true)}
            style={{ cursor: "pointer" }}
          >
            <Tooltip title={<h1>Export All Reportee's Attendance xls</h1>}>
              <img src={excelAllIcon} height={"30rem"} />
            </Tooltip>
          </a>
        </div>
        {openDialogBox && (
          <Dialog open={openDialogBox} onClose={handleClose}>
            <MonthYearPopup onResponse={handleDialogBox} />
          </Dialog>
        )}
      </>
    );
  };

  const createTopBarForSkills = () => {
    return (
      <>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              marginBottom: "1rem",
              fontWeight: "bold",
              fontSize: "1.5rem",
              color: "#1773BC",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <label>SKILLS REPORT FILTER BY FOLLOWING</label>
          </div>
          <div
            style={{ display: "flex", flexDirection: "row" }}
            className="allocationTopBar"
          >
            <div className="dropdownsBox">
              <label style={{ fontSize: "1.25rem" }}> Streams </label>
              <FormControl sx={{ m: "2rem", width: "20rem" }}>
                <InputLabel required id="demo-multiple-checkbox-label">
                  Select Streams
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedStreams}
                  onChange={handleUserChangeStreams}
                  input={<OutlinedInput label="Select User" />}
                  renderValue={getStreamsName}
                  MenuProps={MenuProps}
                >
                  {listItems && (
                    <ListSubheader className="subheader">
                      <TextField
                        placeholder="Search Here..."
                        className="subheader-field"
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        autoFocus={true}
                        onChange={(e) => {
                          setUserStreams(
                            listItems.Streams.filter((i) =>
                              i.longCodeValue
                                ?.toLowerCase()
                                .includes(e.target.value.toLowerCase())
                            )
                          );
                        }}
                      />
                    </ListSubheader>
                  )}
                  <div
                    disabled={!selectedStreamsIds.length}
                    style={{
                      textAlign: "right",
                      cursor: "pointer",
                      height: "2rem",
                    }}
                  >
                    <Link
                      onClick={() => {
                        setSelectedStreams([]);
                        setSelectedStreamsIds("");
                        setSelectAllStreams(false);
                      }}
                      style={{ color: "#1773bc", paddingRight: "1rem" }}
                    >
                      Clear Selection
                    </Link>
                  </div>
                  {/* <MenuItem
                    key={`select-all`}
                    value={"Select All"}
                    className="no-left-margin"
                  >
                    <Checkbox checked={selectAllStreams} />
                    <ListItemText
                      primary={selectAllStreams ? "Select None" : "Select All"}
                    />
                  </MenuItem> */}

                  {userStreams &&
                    userStreams?.map((user) => (
                      <MenuItem
                        key={user}
                        value={user}
                        className="no-left-margin"
                      >
                        <Checkbox
                          checked={
                            selectedStreams.findIndex(
                              (person) => person.id === user.id
                            ) > -1
                          }
                        />
                        <ListItemText primary={user.longCodeValue} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                marginLeft: "1.5rem",
                marginRight: "1.5rem",
              }}
            >
              <span
                style={{ fontSize: "1.5rem", fontWeight: 600, color: "gray" }}
              >
                {" "}
                OR
              </span>
            </div>
            <div className="dropdownsBox">
              <label style={{ fontSize: "1.25rem" }}> Skills Bucket</label>
              <FormControl sx={{ m: "2rem", width: "20rem" }}>
                <InputLabel required id="demo-multiple-checkbox-label">
                  Select Skills
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedSkills}
                  onChange={handleUserChangeSkills}
                  input={<OutlinedInput label="Select User" />}
                  renderValue={getSkillsName}
                  MenuProps={MenuProps}
                >
                  {listItems && (
                    <ListSubheader className="subheader">
                      <TextField
                        placeholder="Search Here..."
                        className="subheader-field"
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        autoFocus={true}
                        onChange={(e) => {
                          setUserSkills(
                            listItems.Skills.filter((i) =>
                              i.longCodeValue
                                ?.toLowerCase()
                                .includes(e.target.value.toLowerCase())
                            )
                          );
                        }}
                      />
                    </ListSubheader>
                  )}
                  <div
                    disabled={!selectedSkillsIds.length}
                    style={{
                      textAlign: "right",
                      cursor: "pointer",
                      height: "2rem",
                    }}
                  >
                    <Link
                      onClick={() => {
                        setSelectedSkills([]);
                        setSelectedSkillsIds("");
                        setSelectAll(false);
                      }}
                      style={{ color: "#1773bc", paddingRight: "1rem" }}
                    >
                      Clear Selection
                    </Link>
                  </div>

                  {/* <MenuItem
                    key={`select-all`}
                    value={"Select All"}
                    className="no-left-margin"
                  >
                    <Checkbox checked={selectAll} />
                    <ListItemText
                      primary={selectAll ? "Select None" : "Select All"}
                    />
                  </MenuItem> */}

                  {userSkills &&
                    userSkills?.map((user) => (
                      <MenuItem
                        key={user}
                        value={user}
                        className="no-left-margin"
                      >
                        <Checkbox
                          checked={
                            selectedSkills.findIndex(
                              (person) => person.id === user.id
                            ) > -1
                          }
                        />
                        <ListItemText primary={user.longCodeValue} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-around",
                marginLeft: "1.5rem",
                marginRight: "1.5rem",
              }}
            >
              <span
                style={{ fontSize: "1.5rem", fontWeight: 600, color: "gray" }}
              >
                {" "}
                OR
              </span>
            </div>
            <div className="dropdownsBox">
              <label style={{ fontSize: "1.25rem" }}>
                {" "}
                Primary/Secondary Skill
              </label>
              <FormControl sx={{ m: "2rem", width: "20rem" }}>
                <InputLabel required id="demo-multiple-checkbox-label">
                  Select Skills
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedPriSecSkills}
                  onChange={handleUserChangePriSecSkills}
                  input={<OutlinedInput label="Select User" />}
                  renderValue={getPriSecSkillsName}
                  MenuProps={MenuProps}
                >
                  {listItems && (
                    <ListSubheader className="subheader">
                      <TextField
                        placeholder="Search Here..."
                        className="subheader-field"
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        autoFocus={true}
                        onChange={(e) => {
                          setUserPriSecSkills(
                            listItems.PriSecSkills.filter((i) =>
                              i.longCodeValue
                                ?.toLowerCase()
                                .includes(e.target.value.toLowerCase())
                            )
                          );
                        }}
                      />
                    </ListSubheader>
                  )}
                  <div
                    disabled={!selectedPriSecSkillsIds.length}
                    style={{
                      textAlign: "right",
                      cursor: "pointer",
                      height: "2rem",
                    }}
                  >
                    <Link
                      onClick={() => {
                        setSelectedPriSecSkills([]);
                        setSelectedPriSecSkillsIds("");
                        setSelectAllPriSecSkill(false);
                      }}
                      style={{ color: "#1773bc", paddingRight: "1rem" }}
                    >
                      Clear Selection
                    </Link>
                  </div>

                  {/* <MenuItem
                    key={`select-all`}
                    value={"Select All"}
                    className="no-left-margin"
                  >
                    <Checkbox checked={selectAllPriSecSkill} />
                    <ListItemText
                      primary={selectAllPriSecSkill ? "Select None" : "Select All"}
                    />
                  </MenuItem> */}

                  {userPriSecSkills &&
                    userPriSecSkills?.map((user) => (
                      <MenuItem
                        key={user}
                        value={user}
                        className="no-left-margin"
                      >
                        <Checkbox
                          checked={
                            selectedPriSecSkills.findIndex(
                              (person) => person.id === user.id
                            ) > -1
                          }
                        />
                        <ListItemText primary={user.longCodeValue} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginLeft: "5rem",
              }}
            >
              {createClearAndShowButtonForSkills()}
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontSize: "1.5rem",
              marginRight: "5rem",
              color: "#1773bc",
            }}
          >
            SkillsList Count: {skillsLength}
          </div>
        </div>
      </>
    );
  };

  const createAddButton = (buttonLabel) => {
    if (
      headingName === Modules.PROJECT_ADMIN &&
      userData.data.roleName === "Project Manager"
    ) {
      // Project Managers can view but not add new projects
      return null;
    } else {
      const isDisabled =
        (headingName === Modules.SOW && (openSOWAddModal || linkProjectsModalOpen || allocationModalOpen || sowEditModalOpen)) ||
        (headingName === Modules.PROJECT_ADMIN && projectEditModalOpen) ||
        (headingName !== Modules.SOW && (isAddButtonClicked || isEditButtonClicked)) ||
        (headingName === Modules.INVOICE_TRACKER && !selectedClientProject);
      return (
        <>
          <button
            disabled={isDisabled}
            className={isDisabled ? "disableAddButton" : "addBtn"}
            onClick={() =>
              headingName === Modules.SOW
                ? setOpenSOWAddModal(true)
                : setIsAddButtonClicked(true)
            }
          >
            <div
              className={isDisabled ? "disableAddButtonText" : "btnText"}
            >
              {buttonLabel}
            </div>
          </button>
        </>
      );
    }
  };

  const createUpdateButton = (buttonLabel) => {
    return (
      <>
        <button
          disabled={isAddButtonClicked || isEditButtonClicked}
          className={
            isAddButtonClicked || isEditButtonClicked
              ? "disableAddButton"
              : "addBtn bulkButton"
          }
          onClick={() => handleUpdateProjectManagement()}
        >
          <div
            className={
              isAddButtonClicked || isEditButtonClicked
                ? "disableAddButtonText"
                : "btnText"
            }
          >
            {buttonLabel}
          </div>
        </button>
      </>
    );
  };

  const handleUpdateProjectManagement = () => {
    setShowUpdateDialog(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleUpdateConfirm = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload", toastOptions);
      return;
    }

    // Get access token for authorization
    const accessToken = getLocalStorageItem(ACCESS_TOKEN);
    if (!accessToken) {
      toast.error('No authorization token found. Please log in again.');
      return;
    }


    const formData = new FormData();
    formData.append("File", selectedFile); // Changed to capital F to match backend expectation

    const refreshParams = {
      projectId: projectId,
      pageNo: 1,
      pageSize: 10,
      sortBy: "projectName",
      sortDir: "ASC",
      searchData: "",
      billStatus: billStatus,
      expiredAllocations: expiredAllocations,
    };

    try {
      const response = await fetch(`${PROJECT_MANAGEMENT_API}/${projectId}/employee-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-correlation-id': v4(),
          // Do not set Content-Type for FormData - let browser set it with boundary
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.failedRows && result.failedRows.length > 0) {
        setErrorDetails(result);
        setGeneralErrorMessage(null);
      } else {
        toast.success(
          result.message || result.Message || "BULK UPDATE SUCCESSFUL!",
          toastOptions
        );
        setShowUpdateDialog(false);
        setSelectedFile(null);
        setFileName("");
        setErrorDetails(null);
        setGeneralErrorMessage(null);
        
        setTimeout(() => {
          dispatch(getProjectManagementData(refreshParams));
        }, 500);
      }
    } catch (err) {
      
      if (err.response) {
        const errorResponse = err.response.data;
        
        if (err.response.status === 400 && errorResponse?.failedRows) {
          setErrorDetails(errorResponse);
          setGeneralErrorMessage(null);
        } else {
          const errorMessage = errorResponse?.errorMessage || 
                              errorResponse?.message || 
                              `Server Error (${err.response.status}): ${err.response.statusText}`;
          setGeneralErrorMessage(errorMessage);
          setErrorDetails(null);
        }
      } else if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        setGeneralErrorMessage(
          "Network Error: Unable to connect to server. Please check your connection and try again."
        );
        setErrorDetails(null);
      } else {
        setGeneralErrorMessage(
          `Request Error: ${err.message || "An unexpected error occurred."}`
        );
        setErrorDetails(null);
      }
    } finally {
      setTimeout(() => {
        dispatch(getProjectManagementData(refreshParams));
      }, 1000);
    }
  };

  const handleUpdateCancel = () => {
    setShowUpdateDialog(false);
    setSelectedFile(null);
    setFileName("");
    setErrorDetails(null);
    setGeneralErrorMessage(null);
  };

  const handleDownloadSampleTemplate = () => {
    let sampleData = [];
    let fileName = "sample_template.xlsx";
    if (headingName === Modules.PROJECT_MANAGEMENT) {
      sampleData = [
        {
          "Employee Id": "20370",
          "Employee Name": "Samarth Sivastava",
          "Project Role": "Backend Developer",
          "Site": "Onshore",
          "Start Date": "2025-08-21",
          "End Date": "2025-08-23",
          "Bill Currency": "USD",
          "Bill Rate": "5",
          "Bill Frequency": "Monthly",
          Allocation: "10",
          "Bill Status": "Billable",
          "Cost Allocation": "100",
        },
        {
          "Employee Id": "20352",
          "Employee Name": "Pratham Mittal",
          "Project Role": "Frontend Developer",
          "Site": "Onshore",
          "Start Date": "2025-08-21",
          "End Date": "2025-08-23",
          "Bill Currency": "USD",
          "Bill Rate": "5",
          "Bill Frequency": "Monthly",
          Allocation: "10",
          "Bill Status": "Billable",
          "Cost Allocation": "100",
        },
      ];
      fileName = "project_management_template.xlsx";
    }

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const headers = Object.keys(sampleData[0]);
    ws["!cols"] = headers.map((h) => {
      const maxLen = Math.max(
        h.length,
        ...sampleData.map((row) => (row[h] ? row[h].toString().length : 0))
      );
      return { wch: maxLen + 2 };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
    toast.success("Sample template downloaded successfully!", toastOptions);
  };

  const excelData = () => {
    if(headingName === Modules.PROJECT_MANAGEMENT){
      return projectManagementData
    }else if(headingName === Modules.SKILLS){
      return skillsList;
    } else if (
      headingName === Modules.REPORTING &&
      tabName === "ALLOCATION CHANGE REPORT BY PROJECT"
    ) {
      return allocationChangeReportData;
    } else if (
      headingName === Modules.REPORTING &&
      tabName === "ALLOCATION CHANGE REPORT BY EMPLOYEE"
    ) {
      return allocationChangeReportDataByEmployee;
    } else if (
      headingName === Modules.REPORTING &&
      tabName === "MISSING TIMESHEET"
    ) {
      return reportingData;
    } else {
      return projectAllocationData;
    }
  };

  const createExpiredAllocation = () => {
    return (
      <>
        <div>
          <label>Expired Allocations</label>
          <Switch
            checked={expiredAllocations}
            onChange={(e) =>
              handleChangeForHistoryInProjectManagement(e.target.value)
            }
            value={expiredAllocations}
            inputProps={{ "aria-label": "controlled" }}
            id="expiredAllocation"
          />
        </div>
      </>
    );
  };

  const createExpiredAllocationforProjectAdmin = () => {
    return (
      <>
        <div>
          <label>Expired Projects</label>
          <Switch
            checked={expiredAllocations}
            onChange={(e) =>
              handleChangeForHistoryInProjectAdmin(e.target.value)
            }
            value={expiredAllocations}
            inputProps={{ "aria-label": "controlled" }}
            id="expiredAllocationForProjectAdmin"
          />
        </div>
      </>
    );
  };

  const createClientFilterForProjectAdmin = () => {
    return (
      <FormControl className="client-filter-dropdown">
        <InputLabel id="client-filter-label">Filter By Client</InputLabel>
        <Select
          labelId="client-filter-label"
          id="client-filter-select"
          value={selectedClientFilter?.id || ""}
          label="Filter By Client"
          onChange={(e) => {
            if (e.target.value === "") {
              setSelectedClientFilter(null);
            } else {
              const selected = clientsData?.find(
                (client) => client.id === e.target.value
              );
              setSelectedClientFilter(selected);
            }
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: { sx: { maxHeight: 300 } },
          }}
        >
          <MenuItem value="">
            <em>All Clients</em>
          </MenuItem>
          {clientsData &&
            clientsData.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    );
  };

  const createSOWFilterSection = () => {
    return (
      <div className="project-admin-filter-section">
        <div className="filter-row">
          {createSearchBox("Search")}
          <FormControl className="client-filter-dropdown" sx={{ minWidth: 220 }}>
            <InputLabel id="sow-client-filter-label">Client</InputLabel>
            <Select
              labelId="sow-client-filter-label"
              id="sow-client-filter-select"
              value={sowListClientFilterId != null ? sowListClientFilterId : ""}
              label="Client"
              onChange={(e) => {
                const v = e.target.value;
                dispatch(setSowListClientFilterId(v === "" ? null : v));
              }}
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 300 } },
              }}
            >
              <MenuItem value="">
                <em>All clients</em>
              </MenuItem>
              {clientsData &&
                clientsData.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      </div>
    );
  };

  const handleClearProjectAdminFilters = () => {
    setSelectedClientFilter(null);
    setExpiredAllocations(false);
    setSearchData("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const createProjectAdminSearchBox = () => {
    return (
      <div className="searchWrapper project-admin-search">
        <img src={searchIcon} className="searchIcon" alt="" />
        <input
          className="searchBox"
          type="search"
          placeholder="Search by Project Name"
          ref={inputRef}
          onChange={setSearchDataHelper}
        />
      </div>
    );
  };

  const createProjectAdminFilterSection = () => {
    const hasActiveFilters = selectedClientFilter || expiredAllocations || searchData;
    return (
      <div className="project-admin-filter-section">
        <div className="filter-row">
          {createProjectAdminSearchBox()}
          {createClientFilterForProjectAdmin()}
          {createExpiredAllocationforProjectAdmin()}
          <button
            className={hasActiveFilters ? "clear-filter-btn active" : "clear-filter-btn"}
            onClick={handleClearProjectAdminFilters}
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>
    );
  };

  // Single source for Project Management allocation list: one API call per project tab (projectId in deps)
  useEffect(() => {
    if (headingName === Modules.PROJECT_MANAGEMENT && projectId != null && projectId !== "") {
      dispatch(
        getProjectManagementData({
          projectId: projectId,
          pageNo: 1,
          pageSize: 1000, // Get more records for client-side bill status filtering
          sortBy: "projectName",
          sortDir: "ASC",
          searchData: searchData,
          billStatus: "All", // Always get all data; bill status filtering is done client-side
          expiredAllocations: expiredAllocations,
        })
      );
    }
  }, [headingName, projectId, expiredAllocations, searchData, dispatch]);

  // Separate useEffect for client-side billStatus filtering; only use data when it matches current projectId
  useEffect(() => {
    if (headingName === Modules.PROJECT_MANAGEMENT && projectManagementData && projectManagementData.data != null && projectManagementData.projectId === projectId) {
      let filteredRows = projectManagementData.data;
      
      if (billStatus && billStatus !== "All") {
        filteredRows = projectManagementData.data.filter(row => {
          return row.billStatus === billStatus;
        });
      }
      
      setRows(filteredRows);
    } else if (headingName === Modules.PROJECT_MANAGEMENT && projectId != null && projectManagementData != null && projectManagementData.projectId !== projectId) {
      setRows([]);
    }
  }, [billStatus, projectManagementData, headingName, projectId]);

  useEffect(() => {
    if (headingName === Modules.PROJECT_ADMIN) {
      dispatch(
        getProjectAdminData({
          projectId: projectId,
          pageNo: 1,
          pageSize: 200,
          sortBy: "projectName",
          sortDir: "ASC",
          searchData: searchData,
          billStatus: billStatus,
          expiredAllocations: expiredAllocations,
        })
      );
    }
  }, [expiredAllocations]);

  // Client-side filtering for Project Admin based on selected client
  useEffect(() => {
    if (headingName === Modules.PROJECT_ADMIN && projectAdminData && projectAdminData.data) {
      let filteredRows = projectAdminData.data;
      
      if (selectedClientFilter && selectedClientFilter.name) {
        filteredRows = projectAdminData.data.filter(row => {
          return row.clientName === selectedClientFilter.name;
        });
      }
      
      setRows(filteredRows);
    }
  }, [selectedClientFilter, projectAdminData, headingName]);

  useEffect(() => {
    const filteredData = rawData.filter((row) => {
      const projectName = findProjectName(row);
      const billStatus = findBillStatus(row);
      const clientName = row.clientName || row.ClientName || 'Unknown Client';

      const projectMatch =
        selectedProjects.length === 0 || selectedProjects.includes(projectName);
      const billStatusMatch =
        selectedBillStatuses.length === 0 ||
        selectedBillStatuses.includes(billStatus);
      const clientMatch =
        selectedClients.length === 0 || selectedClients.includes(clientName);

      return projectMatch && billStatusMatch && clientMatch;
    });

    setData(filteredData);
    
    // Only process chart data for Allocation Dashboard module
    if (headingName === Modules.RESOURCE_ALLOCATION) {
      processDataForCharts(filteredData);
      processExpiringAllocations(filteredData);
      processBenchResources(filteredData);
    }
  }, [
    selectedProjects,
    selectedBillStatuses,
    selectedClients,
    rawData,
    processDataForCharts,
    processExpiringAllocations,
    processBenchResources,
    headingName
  ]);

  // useEffect(() => {
  //   if(headingName=== Modules.PROJECT_MANAGEMENT)
  //   {
  //     dispatch(
  //       getProjectManagementData({
  //         projectId: projectId,
  //         pageNo: 1,
  //         pageSize: 10,
  //         sortBy: "projectName",
  //         sortDir: "ASC",
  //         searchData: searchData,
  //         billStatus: billStatus,
  //         expiredAllocations:expiredAllocations

  //       })
  //     );
  //   }
  // }, [expiredAllocations]);

  // function to handle the state of filtering by bill status
  const handleSelectChange = (event) => {
    setBillStatus(event.target.value);
  };

  const createFilterByBillStatusDropDown = () => {
    return (
      <FormControl>
        <InputLabel id="demo-simple-select-label">
          Filter By Bill Status
        </InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={billStatus}
          label="Filter By Bill Status"
          onChange={handleSelectChange}
          sx={{ minWidth: "15rem", height: "3.5rem" }}
        >
          <MenuItem value={"All"}>All</MenuItem>
          <MenuItem value={"Billable"}>Billable</MenuItem>
          <MenuItem value={"Shadow"}>Shadow</MenuItem>
          <MenuItem value={"NB - Backfilling"}>NB - Backfilling</MenuItem>
          <MenuItem value={"NB - Future SOW"}>NB - Future SOW</MenuItem>
          <MenuItem value={"NB - Additional Scope"}>NB - Additional Scope</MenuItem>
          <MenuItem value={"NB - Productivity Gap"}>NB - Productivity Gap</MenuItem>
          <MenuItem value={"NB - Transition"}>NB - Transition</MenuItem>
          <MenuItem value={"NB - Internal"}>NB - Internal</MenuItem>
          <MenuItem value={"NB - Management"}>NB - Management</MenuItem>
        </Select>
      </FormControl>
    );
  };
  useEffect(() => {
    if (
      headingName === Modules.VIEW_ATTENDANCE &&
      tabName === "VIEW EMPLOYEE ATTENDANCE"
    ) {
      dispatch(getReportees(null));
    }
  }, [tabName]);

  useEffect(() => {
    const fetchData = async () => {
      // try {
      //   const response = await fetch("/data.xlsx");
      //   const arrayBuffer = await response.arrayBuffer();

      //   const workbook = XLSX.read(arrayBuffer, { type: "array" });

      //   let sheetName = "data";
      //   if (!workbook.SheetNames.includes("data")) {
      //     sheetName = workbook.SheetNames[0];
      //   }

      //   const worksheet = workbook.Sheets[sheetName];
      //   const jsonData = XLSX.utils.sheet_to_json(worksheet);

      //   const filteredData = jsonData.filter((row) => {
      //     const billStatus = findBillStatus(row);
      //     return billStatus && billStatus !== "unknown";
      //   });
      //   setRawData(filteredData);
      //   setData(filteredData);

      //   const projects = [
      //     ...new Set(filteredData.map((row) => findProjectName(row))),
      //   ].sort();
      //   const billStatuses = [
      //     ...new Set(filteredData.map((row) => findBillStatus(row))),
      //   ]
      //     .filter((status) => status !== "unknown")
      //     .sort();

      //   setAllProjects(projects);
      //   setAllBillStatuses(billStatuses);
      //   setSelectedProjects(projects);
      //   setSelectedBillStatuses(billStatuses);

      //   processDataForCharts(filteredData);
      //   processExpiringAllocations(filteredData);
      //   processBenchResources(filteredData);
      //   processMonthlyProjections();
      // } catch (error) {
      //   setError(error.message);
      // } finally {
      //   setLoading(false);
      // }
    };

    // fetchData();
    
    // Only fetch data for Allocation Dashboard module
    if (headingName === Modules.RESOURCE_ALLOCATION) {
      dispatch(
        getAllProjectManagementData({
          sortBy: "projectName",
          sortDir: "ASC",
          searchData: "",
        })
      );
    }
  }, [
    processDataForCharts,
    processExpiringAllocations,
    processBenchResources,
    processMonthlyProjections,
    headingName  // Add headingName as dependency
  ]);

  useEffect(()=>{
    if(allProjectManagementData && allProjectManagementData?.data?.length > 0) {
    let projectManagementExcelData = allProjectManagementData.data.map((item) => ({
              zohoEmployeeId: item.zohoEmployeeId,
              employeeName: [item?.firstName, item?.lastName].filter(Boolean).join(" "),
              employeeRole : item.employeeRole,
              clientName: item.clientName,
              projectName: item.projectName,
              projectManagerName: item.projectManagerName,
              site: item.site,
              startDate:{ t: 'd', v: moment(item.startDate).toDate() },
              endDate:{ t: 'd', v: moment(item.endDate).toDate() },
              billStatus: item.billStatus,
              billAllocation: item.billAllocation,
              costAllocation: item.costAllocation,
              department: item.department,
              jobTitle: item.jobTitle,
              stream: item.stream,
              designation: item.designation,
              skillBucket: item.skillBucket,
              primarySkill: item.primarySkill,
              secondarySkill: item.secondarySkill,
              totalExperience: item.totalExperience,
              totalBillableCount: item.totalBillableCount,
              projectAllocationId: item.projectAllocationId,
              lastProject: item.lastProject
              // billDetails: item.billDetails,
            }));
            
            const filteredData = projectManagementExcelData?.filter((row) => {
              const billStatus = findBillStatus(row);
              return billStatus && billStatus !== "unknown";
            });
            setRawData(filteredData);
        setData(filteredData);

        const projects = [
          ...new Set(filteredData?.map((row) => findProjectName(row))),
        ].sort();
        const billStatuses = [
          ...new Set(filteredData?.map((row) => findBillStatus(row))),
        ]
          ?.filter((status) => status !== "unknown")
          .sort();
        const clients = [
          ...new Set(filteredData?.map((row) => row.clientName || row.ClientName || 'Unknown Client')),
        ]
          ?.filter((client) => client !== "Unknown Client")
          .sort();
        setAllProjects(projects);
        setAllBillStatuses(billStatuses);
        setAllClients(clients);
        // Start with no filters applied (show all data)
        setSelectedProjects([]);
        setSelectedBillStatuses([]);
        setSelectedClients([]);
        processDataForCharts(filteredData);
        processExpiringAllocations(filteredData);
        processBenchResources(filteredData);
        
      }
  },[allProjectManagementData]);

  // useEffect(()=>{
  //   processMonthlyProjections();
  // },[])

  const getTotalExpiringCount = () => {
    return Object.values(expiringResources).reduce(
      (total, resources) => total + resources.length,
      0
    );
  };

  const counts = {
    // deviations: deviationData.length,
    expiring: getTotalExpiringCount(),
    projections: monthlyProjections.length,
    bench: benchResources.length,
  };

  const dataInfo = [
    { text: `📊 ${data.length} records analyzed` },
    {
      text: `📋 ${selectedBillStatuses.length}/${allBillStatuses.length} bill status types`,
    },
    { text: `🎯 ${selectedProjects.length}/${allProjects.length} projects` },
    {
      text: `⚠️ ${getTotalExpiringCount()} expiring soon`,
      className: "expiring-badge",
    },
    // {
    //   text: `🚨 ${deviationData.length} deviations`,
    //   className: "deviation-badge",
    // },
    {
      text: `📈 ${monthlyProjections.length} month projections`,
      className: "projections-badge",
    },
  ];

  return (
    <>
      {/* Guard clause: Show loading if userData is not available */}
      {!userData || !userData.data ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <p>Loading user data...</p>
        </div>
      ) : (
        <div
          className={
            tabName === "VIEW EMPLOYEE ATTENDANCE"
              ? `tableDivViewAttendance`
              : `tableDiv ${tabName === "MY TIMESHEET" ? "timesheetTable" : ""}`
          }
          // style={tabName === "VIEW EMPLOYEE ATTENDANCE" ? { padding: "0.5rem!important" } : {}}
        >
      <div
        className={
          headingName === Modules.REPORTING
            ? "searchHeaderFlex"
            : "searchHeader"
        }
      >
        {headingName === Modules.PROJECT_ALLOCATION &&
          tabName === "RESOURCE ALLOCATION" &&
          createTopBarForProjectAllocation()}
        {headingName !== Modules.TIMESHEET &&
          headingName !== Modules.REPORTING &&
          headingName !== Modules.VIEW_ATTENDANCE &&
          headingName !== Modules.TRAINING_AND_SESSION &&
          headingName !== Modules.SESSION_FEEDBACK &&
          headingName !== Modules.INVOICE_TRACKER &&
          headingName !== Modules.CORRECT_ATTENDANCE &&
          headingName !== Modules.PROJECT_ADMIN &&
          tabName !== "RESOURCE ALLOCATION" &&
          headingName !== Modules.SKILLS &&
          tabName !== "SKILLS REPORT" &&
          headingName !== Modules.RESOURCE_ALLOCATION &&
          headingName !== Modules.SOW &&
          createSearchBox("Search")}
        {headingName === Modules.SOW && createSOWFilterSection()}
        {headingName === Modules.TRAINING_AND_SESSION &&
          tabName === "TRAINING AND SESSION" &&
          createTopBarForSessionAndTraining("Search")}
        {headingName === Modules.PROJECT_MANAGEMENT &&
          createFilterByBillStatusDropDown()}
        {headingName === Modules.PROJECT_MANAGEMENT &&
          createExpiredAllocation()}
        {headingName === Modules.PROJECT_ADMIN &&
          createProjectAdminFilterSection()}
        {headingName === Modules.INVOICE_TRACKER &&
          tabName === "CREATE INVOICE" &&
          createTopBarForInvoice()}
        {headingName === Modules.INVOICE_TRACKER &&
          tabName === "INVOICE DETAILS" &&
          createTopBarForInvoice_Details()}
        <div className="tabs-table-btns">
        {headingName === Modules.TIMESHEET
          ? tabName === "MY TIMESHEET"
            ? myTimeSheetTab()
            : pendingApprovalReportessTab()
          : headingName === Modules.REPORTING
          ? FilterData()
          : headingName === Modules.SOW ||
            ((headingName !== Modules.PROJECT_ALLOCATION) &&
            (headingName !== Modules.INVOICE_TRACKER) &&
            (headingName !== Modules.SKILLS) &&
            (headingName !== Modules.VIEW_ATTENDANCE) &&
            (headingName !== Modules.TRAINING_AND_SESSION || tabName !== "SESSION CREATION") &&
            (headingName !== Modules.SESSION_FEEDBACK) &&
            (headingName !== Modules.RESOURCE_ALLOCATION) &&
            (headingName !== Modules.CORRECT_ATTENDANCE))
          ? createAddButton(headingName === Modules.PROJECT_ADMIN ? "Add Project" : headingName === Modules.SOW ? "Add SOW" : "Add")
          : null}
        {headingName === Modules.PROJECT_MANAGEMENT ? createViewButton("View Projections"): null}
        {headingName === Modules.PROJECT_MANAGEMENT &&
                createUpdateButton("Bulk Upload")}
        {headingName === Modules.PROJECT_MANAGEMENT && showDialogBox &&(
            <div className="dialog-overlay">
              <dialog
              open="true"
              onClose={handleClose}
              aria-labelledby="responsive-dialog-title"
              maxWidth={"false"}
              className="view-dialog-container"             
            >
              <div className="wrapper view-dialog">
              <ProjectionsView monthlyProjections={monthlyProjections} name={tabName} />
                <div className="dialogActions">
                  <Button autoFocus onClick={handleViewModalClose}  variant="contained"  size="large">
                    Cancel
                  </Button>
                </div>
              </div>
            </dialog>
            </div>
        )}  
        </div>  
        {/* {headingName !== Modules.SKILLS && tabName !== 'SKILLS REPORT' &&  createSearchBox("Search")} */}
        {/* {headingName===Modules.SESSION_FEEDBACK&&(<SessionFeedback/>)} */}
        {headingName === Modules.SKILLS &&
          tabName === "SKILLS REPORT" &&
          createTopBarForSkills()}
        {headingName === Modules.VIEW_ATTENDANCE &&
          tabName === "VIEW EMPLOYEE ATTENDANCE" &&
          createTopBarForViewAttendance()}
      </div>
      {opencreateSessionPopup && (
        <Dialog
          open={opencreateSessionPopup}
          onClose={handleClose}
          maxWidth={"false"}
          PaperProps={{
            style: {
              backgroundColor: "white",
              padding: "2.3rem",
              marginLeft: "20rem",
            },
          }}
        >
          {headingName === Modules.TRAINING_AND_SESSION && (
            <SessionCreation onResponse={handleMultipleFileView} />
          )}
        </Dialog>
      )}
      {headingName === Modules.SOW && (
        <SOWAddModal
          open={openSOWAddModal}
          onClose={() => setOpenSOWAddModal(false)}
        />
      )}
      {headingName === Modules.SOW && (
        <LinkProjectsModal
          open={linkProjectsModalOpen}
          onClose={() => {
            setLinkProjectsModalOpen(false);
            setLinkProjectsSowRow(null);
          }}
          sow={linkProjectsSowRow}
          onSuccess={() => {
            setLinkProjectsModalOpen(false);
            setLinkProjectsSowRow(null);
          }}
        />
      )}
      {headingName === Modules.SOW && (
        <SOWAllocationModal
          open={allocationModalOpen}
          onClose={() => {
            setAllocationModalOpen(false);
            setAllocationSowRow(null);
          }}
          sow={allocationSowRow}
          onSuccess={() => {
            setAllocationModalOpen(false);
            setAllocationSowRow(null);
          }}
        />
      )}
      {headingName === Modules.SOW && (
        <SOWEditModal
          open={sowEditModalOpen}
          onClose={() => {
            setSowEditModalOpen(false);
            setSowEditModalRow(null);
          }}
          sowRow={sowEditModalRow}
          onSuccess={() => {
            setSowEditModalOpen(false);
            setSowEditModalRow(null);
          }}
        />
      )}
      {headingName === Modules.PROJECT_ADMIN && (
        <ProjectEditModal
          open={projectEditModalOpen}
          onClose={() => {
            setProjectEditModalOpen(false);
            setProjectEditModalRow(null);
          }}
          projectRow={projectEditModalRow}
          pageNo={0}
          pageSize={100}
          expiredAllocations={expiredAllocations}
          onSuccess={() => {
            setProjectEditModalOpen(false);
            setProjectEditModalRow(null);
          }}
        />
      )}
        
        {headingName === Modules.CORRECT_ATTENDANCE ? (
          <CorrectAttendanceForm />
        ) : headingName === Modules.TRAINING_AND_SESSION && tabName === "REPORTING DASHBOARD" ? (
          <SessionReporting />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            headingName={headingName}
            tabName={tabName}
            setColumns={setColumns}
            setRows={setRows}
            totalRecord={totalRecord}
            isAddButtonClicked={isAddButtonClicked}
            setIsAddButtonClicked={setIsAddButtonClicked}
            isEditButtonClicked={isEditButtonClicked}
            setIsEditButtonClicked={setIsEditButtonClicked}
            searchData={searchData}
            resetSearchData={resetSearchData}
            projectStatus={status}
            selectedPeriodWeek={selectedPeriodWeek}
            projectId={projectId}
            rowToBeUpdated={rowToBeUpdated}
            setRowToBeUpdated={setRowToBeUpdated}
            billStatus={billStatus}
            expiredAllocations={expiredAllocations}
            dataForExcel={excelData()}
            selectedUserIds={
              headingName === Modules.REPORTING
              ? selectedUserIds
              : headingName === Modules.SKILLS
              ? selectedSkillsIds
              : selectedUserIds
            }
            isHistory={isHistory}
            startDate={startDate}
            endDate={endDate}
            selectedUserID={selectedUserID}
            viewAttendanceClicked={viewAttendanceClicked}
            selectedTimesheetRows={selectedTimesheetRows}
            setSelectedTimesheetRows={setSelectedTimesheetRows}
            onLinkProjectsClick={
              headingName === Modules.SOW
                ? (row) => {
                    setLinkProjectsSowRow(row);
                    setLinkProjectsModalOpen(true);
                  }
                : undefined
            }
            onAllocationClick={
              headingName === Modules.SOW
                ? (row) => {
                    setAllocationSowRow(row);
                    setAllocationModalOpen(true);
                  }
                : undefined
            }
            onSOWEditClick={
              headingName === Modules.SOW
                ? (row) => {
                    setSowEditModalRow(row);
                    setSowEditModalOpen(true);
                  }
                : undefined
            }
            onProjectEditClick={
              headingName === Modules.PROJECT_ADMIN
                ? (row) => {
                    setProjectEditModalRow(row);
                    setProjectEditModalOpen(true);
                  }
                : undefined
            }
          />
        )}
      {/* Total Hours moved to DataTable.js for MY TIMESHEET */}
      {/* {headingName===Modules.REPORTING && <ExportExcel data={headingName===Modules.REPORTING ? projectManagementData : []} headingName={headingName} projectId={projectId}/>} */}
      {headingName === Modules.VIEW_ATTENDANCE &&
      viewAttendanceData &&
      rows.length > 0 ? (
        <div></div>
      ) : (
        <div></div>
      )}
      {headingName === Modules.RESOURCE_ALLOCATION &&
        tabName === "Dashboard" && (
          <>
            {/* Project Filter Section */}
            <div className="resource-filters" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              padding: '1.5rem', 
              margin: '0 0 2rem 0', 
              borderRadius: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0', 
                color: '#2c3e50',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}>
                🔍 Filter Dashboard Data
              </h3>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: '250px' }}>
                  <InputLabel>Select Projects</InputLabel>
                  <Select
                    multiple
                    value={selectedProjects}
                    onChange={(event) => setSelectedProjects(event.target.value)}
                    input={<OutlinedInput label="Select Projects" />}
                    renderValue={(selected) => 
                      selected.length === 0 
                        ? 'All Projects' 
                        : selected.length === allProjects.length 
                        ? 'All Projects'
                        : `${selected.length} selected`
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value=""
                      onClick={() => {
                        if (selectedProjects.length === allProjects.length) {
                          setSelectedProjects([]);
                        } else {
                          setSelectedProjects([...allProjects]);
                        }
                      }}
                      style={{ fontWeight: 'bold' }}
                    >
                      <Checkbox 
                        checked={selectedProjects.length === allProjects.length}
                        indeterminate={selectedProjects.length > 0 && selectedProjects.length < allProjects.length}
                      />
                      <ListItemText primary="Select All Projects" />
                    </MenuItem>
                    {allProjects.map((project) => (
                      <MenuItem key={project} value={project}>
                        <Checkbox checked={selectedProjects.indexOf(project) > -1} />
                        <ListItemText primary={project} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: '200px' }}>
                  <InputLabel>Bill Status</InputLabel>
                  <Select
                    multiple
                    value={selectedBillStatuses}
                    onChange={(event) => setSelectedBillStatuses(event.target.value)}
                    input={<OutlinedInput label="Bill Status" />}
                    renderValue={(selected) => 
                      selected.length === 0 
                        ? 'All Statuses' 
                        : selected.length === allBillStatuses.length 
                        ? 'All Statuses'
                        : `${selected.length} selected`
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 250,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value=""
                      onClick={() => {
                        if (selectedBillStatuses.length === allBillStatuses.length) {
                          setSelectedBillStatuses([]);
                        } else {
                          setSelectedBillStatuses([...allBillStatuses]);
                        }
                      }}
                      style={{ fontWeight: 'bold' }}
                    >
                      <Checkbox 
                        checked={selectedBillStatuses.length === allBillStatuses.length}
                        indeterminate={selectedBillStatuses.length > 0 && selectedBillStatuses.length < allBillStatuses.length}
                      />
                      <ListItemText primary="Select All Statuses" />
                    </MenuItem>
                    {allBillStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox checked={selectedBillStatuses.indexOf(status) > -1} />
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: '200px' }}>
                  <InputLabel>Client Name</InputLabel>
                  <Select
                    multiple
                    value={selectedClients}
                    onChange={(event) => setSelectedClients(event.target.value)}
                    input={<OutlinedInput label="Client Name" />}
                    renderValue={(selected) => 
                      selected.length === 0 
                        ? 'All Clients' 
                        : selected.length === allClients.length 
                        ? 'All Clients'
                        : `${selected.length} selected`
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 250,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value=""
                      onClick={() => {
                        if (selectedClients.length === allClients.length) {
                          setSelectedClients([]);
                        } else {
                          setSelectedClients([...allClients]);
                        }
                      }}
                      style={{ fontWeight: 'bold' }}
                    >
                      <Checkbox 
                        checked={selectedClients.length === allClients.length}
                        indeterminate={selectedClients.length > 0 && selectedClients.length < allClients.length}
                      />
                      <ListItemText primary="Select All Clients" />
                    </MenuItem>
                    {allClients.map((client) => (
                      <MenuItem key={client} value={client}>
                        <Checkbox checked={selectedClients.indexOf(client) > -1} />
                        <ListItemText primary={client} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedProjects([]);
                    setSelectedBillStatuses([]);
                    setSelectedClients([]);
                  }}
                  sx={{ 
                    height: '56px',
                    minWidth: '120px',
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#5a6fd8',
                      backgroundColor: 'rgba(102, 126, 234, 0.05)'
                    }
                  }}
                >
                  Clear Filters
                </Button>

                <div style={{ 
                  fontSize: '0.9rem', 
                  color: '#7f8c8d',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem'
                }}>
                  <span>📊 {selectedProjects.length === 0 ? allProjects.length : selectedProjects.length} / {allProjects.length} projects</span>
                  <span>📋 {selectedBillStatuses.length === 0 ? allBillStatuses.length : selectedBillStatuses.length} / {allBillStatuses.length} bill statuses</span>
                  <span>🏢 {selectedClients.length === 0 ? allClients.length : selectedClients.length} / {allClients.length} clients</span>
                </div>
              </div>
            </div>

            <ResourceDashboardView
              resourceCountData={resourceCountData}
              billStatusSummary={billStatusSummary}
              projectSummary={projectSummary}
              projectAllocationData={{ data: data }} // Pass the filtered allocation data for enhanced analysis
            />
          </>
        )}
      {/* {headingName === Modules.RESOURCE_ALLOCATION &&
        tabName === "Deviations" && (
          <DeviationsView deviationData={deviationData} />
        )} */}
      {headingName === Modules.RESOURCE_ALLOCATION && (
        <>
          {/* For Project Manager, only show Expiring Allocations and Bench Resources */}
          {userData.data.roleName === "Project Manager" ? (
            <>
              {tabName === "expiring allocations" && (
                <ExpiringView expiringResources={expiringResources} />
              )}
              {tabName === "Bench Resources" && (
                <BenchView
                  benchResources={benchResources}
                  benchStreamData={benchStreamData}
                  allProjectAllocations={allProjectManagementData}
                />
              )}
            </>
          ) : (
            <>
              {/* For Admin and other roles, show all tabs */}
              {tabName === "RESOURCE ALLOCATION" && (
                <>
                  <ResourceDashboardView
                    resourceCountData={resourceCountData}
                    billStatusSummary={billStatusSummary}
                    projectSummary={projectSummary}
                    projectAllocationData={{ data: data }}
                  />
                </>
              )}
              {tabName === "expiring allocations" && (
                <ExpiringView expiringResources={expiringResources} />
              )}
              {tabName === "Bench Resources" && (
                <BenchView
                  benchResources={benchResources}
                  benchStreamData={benchStreamData}
                  allProjectAllocations={allProjectManagementData}
                />
              )}
              {tabName === "Multi Projects Allocation" && <MultiProjectView data={rawData} />}
            </>
          )}
        </>
      )}

      {/* Bulk Upload Dialog */}
      <Dialog
        open={showUpdateDialog}
        onClose={handleUpdateCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            padding: "24px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle>
          <div style={{ textAlign: "center", fontSize: "2.4rem", fontWeight: "bold", color: "#1976d2", marginBottom: "8px" }}>
            Bulk Upload Allocation
          </div>
          {headingName === Modules.PROJECT_MANAGEMENT && tabName && (
            <div style={{ textAlign: "center", fontSize: "2rem", color: "#666", fontWeight:"bold"}}>
              Project: {tabName}
            </div>
          )}
        </DialogTitle>
        <DialogContent>
          <div style={{ padding: "0 24px" }}>
            {/* Error Section - shows when there are errors */}
            {(errorDetails || generalErrorMessage) && (
              <div style={{ 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '20px',
                margin: '0 20px 20px 20px'
              }}>
                <h4 style={{ color: '#e65100', marginTop: 0, marginBottom: '12px' }}>
                  Upload Results
                </h4>
                
                {errorDetails && (
                  <>
                    <div style={{ fontSize: '1.3rem', lineHeight: '1.6', marginBottom: '16px' }}>
                      <div style={{ color: '#2e7d32', fontWeight: 'bold', marginBottom: '5px' }}>
                        ✅ <strong>{errorDetails.successCount || (errorDetails.totalRecords - (errorDetails.failedRows?.length || 0))} records</strong> successfully inserted
                      </div>
                      <div style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        ❌ <strong>{errorDetails.failedCount || errorDetails.failedRows?.length || 0} records</strong> failed with errors
                      </div>
                    </div>
e
                    {errorDetails.failedRows?.length > 0 && (
                      <>
                        <h5 style={{ color: '#d32f2f', marginBottom: '8px', fontSize: '1.4rem' }}>
                          Failed Records:
                        </h5>
                        <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white'
                        }}>
                          {errorDetails.failedRows.map((row, index) => (
                            <div 
                              key={index}
                              style={{ 
                                padding: '8px 12px', 
                                borderBottom: index < errorDetails.failedRows.length - 1 ? '1px solid #eee' : 'none',
                                backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                              }}
                            >
                              <strong style={{ color: '#d32f2f', fontSize: '1.2rem' }}>Row {row.rowNumber}</strong>
                              {(row.employeeName || row.employeeId) && (
                                <span style={{ color: '#666', marginLeft: '8px', fontSize: '1.1rem' }}>
                                  {row.employeeName ? `${row.employeeName} ( ${row.employeeId || 'N/A'} )` : 
                                   row.employeeId ? `${getEmployeeName(row.employeeId) || 'Unknown Employee'} ( ${row.employeeId} )` : 
                                   'Unknown Employee'}
                                </span>
                              )}
                              <div style={{ 
                                marginTop: '4px', 
                                color: '#333', 
                                fontSize: '1.1rem',
                                fontStyle: 'italic'
                              }}>
                                {row.reason}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}

                {generalErrorMessage && (
                  <div style={{ color: '#d32f2f', fontSize: '1.3rem', lineHeight: '1.5' }}>
                    {generalErrorMessage}
                  </div>
                )}
              </div>
            )}

            <p
              style={{
                marginBottom: "20px",
                color: "#666",
                fontSize: "1.4rem",
                textAlign: "left",
              }}
            >
              Please select an Excel file (.xlsx, .xls) or CSV file (.csv) to upload project allocations.
            </p>

            <div
              style={{
                border: "2px dashed #ddd",
                borderRadius: "8px",
                padding: "40px",
                textAlign: "center",
                backgroundColor: "#fafafa",
              }}
            >
              <input
                type="file"
                id="file-upload"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                accept=".xlsx,.xls,.csv"
              />
              <label
                htmlFor="file-upload"
                style={{
                  cursor: "pointer",
                  display: "block",
                }}
              >
                <div
                  style={{
                    color: "#1976d2",
                    fontSize: "40px",
                    marginBottom: "12px",
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 40 }} />
                </div>
                <div
                  style={{
                    color: "#999",
                    fontSize: "1.4rem",
                  }}
                >
                  Click the attachment button to select a file
                </div>
              </label>

              <div style={{ marginTop: "16px" }}>
                {fileName ? (
                  <p style={{ color: "#1976d2", fontWeight: "bold", fontSize: "1.4rem" }}>
                    Selected: {fileName}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions
          style={{
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleDownloadSampleTemplate();
            }}
            variant="outlined"
            startIcon={<AttachFileIcon />}
            style={{
              borderColor: "#1976d2",
              color: "#1976d2",
              padding: "10px 20px",
              fontSize: "1.2rem",
              textTransform: "none",
              borderRadius: "4px",
              minWidth: "140px",
              height: "40px",
            }}
          >
            Download Sample
          </Button>
          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              onClick={handleUpdateCancel}
              variant="outlined"
              style={{
                borderColor: "#d32f2f",
                color: "#d32f2f",
                backgroundColor: "#ffebee",
                padding: "10px 20px",
                fontSize: "1.2rem",
                textTransform: "none",
                borderRadius: "4px",
                minWidth: "140px",
                height: "40px",
              }}
            >
              {(errorDetails || generalErrorMessage) ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={handleUpdateConfirm}
              variant="contained"
              style={{
                backgroundColor: selectedFile ? "#1976d2" : "#ccc",
                color: "white",
                padding: "10px 20px",
                fontSize: "1.2rem",
                textTransform: "none",
                borderRadius: "4px",
                minWidth: "140px",
                height: "40px",
                cursor: selectedFile ? "pointer" : "not-allowed",
              }}
              disabled={!selectedFile}
            >
              {(errorDetails || generalErrorMessage) ? "Try Again" : "Update Allocation"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
      )}
    </>
  );
};
